import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { Bars3BottomLeftIcon } from "@heroicons/react/24/outline";
import Sidebar, { ContentItem } from "../components/Sidebar";
import InputBar, { type ChatDomain } from "../components/InputBar";
import ContentContainer from "../components/ContentContainer";
import { useAuthContext } from "../contexts/AuthContext";
import styles from "../components/Chat.module.css";
import AccountMenu from "../components/AccountMenu";
import HeaderQBankLink from "../components/HeaderQBankLink";
import { getChatEntry, medicineChatPath } from "../utils/chatEntry";
import SEO from "../components/SEO";
import AnnouncementBanner from "../components/AnnouncementBanner";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { useChatLayoutState } from "../hooks/useChatLayoutState";
import { useCreditsSubscription } from "../hooks/useCreditsSubscription";
import { ServerSentEventParser } from "../utils/sse";
import {
  extractStreamingJsonArrayObjects,
  extractStreamingJsonString,
  isDisplayableReference,
  isStructuredThreadTurn,
  StreamingContentRevisionBuffer,
} from "../utils/threadStreaming";
import {
  getToolActivityCallId,
  toolActivityUpdateFromEvent,
} from "../utils/toolActivity";
import {
  classifyRequestedGeneration,
  generationAttemptKey,
  generationPromptDigest,
  hasProcessingRecoveryExpired,
  isDefinitiveGenerationCapacityRejection,
  requestedGenerationIdForAttempt,
  runWithAbortTimeout,
  shouldPollAfterStreamInactivity,
  shouldRetryGenerationAcceptance,
  type UnresolvedGenerationAttempt,
} from "../utils/chatRecovery";
import {
  normalizeChatReaction,
  type ChatReaction,
  type ChatReactionValue,
  type DislikeReasonCode,
} from "../types/chatFeedback";

// Message type definition
interface ThreadMessage {
  message_id: string;
  content: string | any;
  sender: "user" | "ai";
  timestamp: string;
  // Flag to indicate AI tool-call bubbles (rendered without logo/timestamp)
  isToolCall?: boolean;
  // Agent6 investigation notes are regular-looking assistant messages that
  // remain separate from both tool activity and the final answer.
  isAgentCommentary?: boolean;
  commentaryId?: string;
  commentaryRawContent?: string;
  isCommentaryStreaming?: boolean;
  generationId?: string;
  answer?: string;
  justification?: string;
  references?: Array<{
    citedText: string;
    reference: string;
    pageNumber: string;
    type?: string;
    details?: any;
  }>;
  savedNoteId?: string | null;
  userReaction?: ChatReaction | null;
  // Processing status for background message completion
  processing_status?: "pending" | "processing" | "complete" | "error";
  error_message?: string;
  progressSteps?: Array<{
    id: string;
    label: string;
    kind?: "status" | "commentary";
  }>;
}

interface ActiveThreadProcessing {
  messageId: string;
  generationId: string;
  requestToken: string;
  status: "pending" | "processing" | "error";
  label: string;
  errorMessage?: string;
}

interface ActiveStream {
  requestToken: string;
  kind: "thread" | "question";
  threadId?: string;
  controller: AbortController;
  reader: ReadableStreamDefaultReader<Uint8Array> | null;
  lastActivityAt: number;
}

interface ProcessingPoll {
  key: string;
  threadId: string;
  messageId: string;
  generationId: string;
  requestToken: string;
  timer: ReturnType<typeof setTimeout> | null;
  controller: AbortController | null;
  stopped: boolean;
  startedAt: number;
  unreachableSince: number | null;
}

interface ActivityProgressStep {
  label: string;
  kind?: "status" | "commentary";
}

// Question data type definition
interface QuestionData {
  questionText?: string;
  specialty?: string;
  status?: string;
  answer?: string;
  justification?: string;
  references?: Array<{
    citedText: string;
    reference: string;
    pageNumber: string;
  }>;
}

const PROCESSING_STATUS_UNREACHABLE_TIMEOUT_MS = 3 * 60 * 1000;
const PROCESSING_STATUS_POLL_TIMEOUT_MS = 12 * 60 * 1000;
const PROCESSING_STATUS_REQUEST_TIMEOUT_MS = 15 * 1000;
// Transport comment heartbeats keep proxies alive but do not prove that the
// generation itself is advancing. Start exact-ID status recovery after this
// much time without a real SSE data event.
const SSE_ACTIVITY_TIMEOUT_MS = 45 * 1000;
const STREAM_ACCEPTANCE_TIMEOUT_MS = 30 * 1000;
const STREAM_ACCEPTANCE_RECOVERY_GRACE_MS = 30 * 1000;
const STREAM_ACCEPTANCE_STATUS_TIMEOUT_MS = 5 * 1000;
const CHAT_CONTROL_REQUEST_TIMEOUT_MS = 15 * 1000;
const UNRESOLVED_GENERATION_STORAGE_PREFIX =
  "cshaped.chat-generation-attempt.v1:";

const unresolvedGenerationStorageKey = (attemptKey: string) =>
  `${UNRESOLVED_GENERATION_STORAGE_PREFIX}${attemptKey}`;

const loadUnresolvedGenerationAttempts = () => {
  const attempts = new Map<string, UnresolvedGenerationAttempt>();
  if (typeof window === "undefined") return attempts;

  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const storageKey = window.localStorage.key(index);
      if (!storageKey?.startsWith(UNRESOLVED_GENERATION_STORAGE_PREFIX)) continue;
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) continue;
      let attempt: Partial<UnresolvedGenerationAttempt>;
      try {
        attempt = JSON.parse(raw) as Partial<UnresolvedGenerationAttempt>;
      } catch {
        continue;
      }
      if (
        typeof attempt.userId !== "string" ||
        typeof attempt.threadId !== "string" ||
        typeof attempt.promptDigest !== "string" ||
        typeof attempt.generationId !== "string" ||
        typeof attempt.createdAt !== "string"
      ) {
        continue;
      }
      const key = generationAttemptKey(
        attempt.userId,
        attempt.threadId,
        attempt.promptDigest
      );
      attempts.set(key, attempt as UnresolvedGenerationAttempt);
    }
  } catch (error) {
    console.error("Unable to restore chat generation retry identity:", error);
  }
  return attempts;
};

const persistUnresolvedGenerationAttempt = (
  key: string,
  attempt: UnresolvedGenerationAttempt
) => {
  try {
    window.localStorage.setItem(
      unresolvedGenerationStorageKey(key),
      JSON.stringify(attempt)
    );
  } catch (error) {
    console.error("Unable to persist chat generation retry identity:", error);
  }
};

const removePersistedUnresolvedGenerationAttempt = (key: string) => {
  try {
    window.localStorage.removeItem(unresolvedGenerationStorageKey(key));
  } catch (error) {
    console.error("Unable to clear chat generation retry identity:", error);
  }
};

// Helper function to remove .pdf extension from reference names
const removeFileExtension = (fileName: string): string => {
  if (!fileName) return fileName;
  return fileName.replace(/\.pdf$/i, "");
};

const isActiveProcessingStatus = (status?: ThreadMessage["processing_status"]) =>
  status === "pending" || status === "processing";

const hasRenderableAssistantContent = (message: ThreadMessage) => {
  if (message.answer || message.justification) return true;
  if (message.references && message.references.length > 0) return true;
  if (typeof message.content === "string") {
    return message.content.trim().length > 0;
  }
  return Boolean(message.content);
};

const isBlankProcessingAssistant = (message: ThreadMessage) =>
  message.sender === "ai" &&
  !message.isToolCall &&
  isActiveProcessingStatus(message.processing_status) &&
  !hasRenderableAssistantContent(message);

// Utility function to transform raw message data
const transformMessage = (msg: any): ThreadMessage => {
  // Render a simple bubble for tool calls so users can see investigated concepts
  if (msg.type === "tool_call" && msg.role === "assistant") {
    try {
      const tc =
        typeof msg.content === "string" ? JSON.parse(msg.content) : msg.content;
      const fn = tc?.function?.name || tc?.name || "unknown";
      const callId = tc?.call_id || tc?.id;
      const activity = tc?.activity || {};
      const activityStatus =
        msg.processing_status === "error"
          ? "error"
          : activity?.status === "running" ||
          activity?.status === "error" ||
          activity?.status === "complete"
          ? activity.status
          : isActiveProcessingStatus(msg.processing_status)
            ? "running"
            : msg.processing_status === "error"
              ? "error"
              : "complete";
      const resultsCount =
        typeof activity?.results_count === "number"
          ? activity.results_count
          : undefined;
      const activityError =
        typeof activity?.error_message === "string"
          ? activity.error_message
          : undefined;
      const activityGenerationId =
        typeof activity?.generation_id === "string"
          ? activity.generation_id
          : undefined;
      // Special handling for built-in web search calls persisted by the backend
      if (fn === "web_search" || tc?.type === "web_search_call") {
        const argsStr = tc?.function?.arguments ?? tc?.arguments ?? "{}";
        let query = "";
        try {
          const args = JSON.parse(argsStr);
          query = args?.query || "the web";
        } catch { }
        return {
          message_id: msg.message_id,
          content: {
            kind: "web_search",
            query,
            callId,
            generationId: activityGenerationId,
            status: activityStatus,
            resultsCount,
            errorMessage: activityError,
          },
          sender: "ai",
          timestamp: msg.created_at,
          isToolCall: true,
          processing_status: msg.processing_status,
        };
      }
      const argsStr = tc?.function?.arguments ?? tc?.arguments ?? "{}";
      const args = JSON.parse(argsStr);
      const concept = args?.concept || "unknown concept";
      const collection = args?.collection_name || "selected collection";
      const toolType = args?.source_type || (fn.includes("textbooks")
        ? "textbooks"
        : fn.includes("article")
          ? "articles"
          : undefined);
      return {
        message_id: msg.message_id,
        content: {
          kind: "knowledge_lookup",
          concept,
          collection,
          toolType,
          callId,
          generationId: activityGenerationId,
          status: activityStatus,
          resultsCount,
          errorMessage: activityError,
        },
        sender: "ai",
        timestamp: msg.created_at,
        // Mark as a tool-call bubble so UI renders it without logo/timestamp
        isToolCall: true,
        processing_status: msg.processing_status,
      };
    } catch {
      // Fallback: show a generic tool lookup bubble
      return {
        message_id: msg.message_id,
        content: { kind: "knowledge_lookup", concept: "a concept" },
        sender: "ai",
        timestamp: msg.created_at,
        isToolCall: true,
      };
    }
  }

  if (msg.type === "agent_commentary" && msg.role === "assistant") {
    let commentary = msg.content;
    if (typeof commentary === "string") {
      try {
        commentary = JSON.parse(commentary);
      } catch {
        // Older/fallback rows may contain the conversational text directly.
      }
    }
    const commentaryText =
      commentary && typeof commentary === "object"
        ? commentary.text || commentary.content || ""
        : commentary;
    return {
      message_id: msg.message_id,
      content:
        typeof commentaryText === "string"
          ? commentaryText
          : JSON.stringify(commentaryText),
      sender: "ai",
      timestamp: msg.created_at,
      isAgentCommentary: true,
      commentaryId:
        commentary && typeof commentary === "object"
          ? commentary.commentary_id
          : undefined,
      commentaryRawContent:
        typeof commentaryText === "string" ? commentaryText : undefined,
      isCommentaryStreaming: false,
      generationId:
        commentary && typeof commentary === "object"
          ? commentary.generation_id
          : undefined,
      processing_status: "complete",
    };
  }

  const baseMessage: ThreadMessage = {
    message_id: msg.message_id,
    content: msg.content,
    sender: msg.role === "user" ? "user" : "ai",
    timestamp: msg.created_at,
    savedNoteId: msg.saved_note_id ?? null,
    userReaction: normalizeChatReaction(msg.user_reaction),
    processing_status: msg.processing_status,
    error_message: msg.error_message,
  };

  let parsedContent: any = null;
  let preliminaryText = "";
  let hasPreliminaryText = false;

  const tryParse = (str: string) => {
    try {
      return JSON.parse(str);
    } catch {
      /* Attempt to repair common issues like raw newlines within strings */
      try {
        const repaired = str.replace(/\n/g, " ");
        return JSON.parse(repaired);
      } catch {
        return null;
      }
    }
  };

  const extractJsonSubstring = (
    text: string
  ): { json: string; start: number } | null => {
    const firstBrace = text.indexOf("{");
    const lastBrace = text.lastIndexOf("}");
    if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace)
      return null;
    const jsonSubstring = text.substring(firstBrace, lastBrace + 1);
    return { json: jsonSubstring, start: firstBrace };
  };

  // 1) Try entire string first
  parsedContent = tryParse(msg.content);
  if (parsedContent) {
    hasPreliminaryText = false;
  } else {
    // 2) Look for fenced code block
    const jsonMatch = msg.content.match(/```json\s*(\{[\s\S]*?\})\s*```/);
    if (jsonMatch) {
      parsedContent = tryParse(jsonMatch[1]);
      const jsonBlockStart = msg.content.indexOf("```json");
      preliminaryText = msg.content.substring(0, jsonBlockStart).trim();
      hasPreliminaryText = preliminaryText.length > 0;
    }

    // 3) If still not parsed, attempt generic object regex (original logic)
    if (!parsedContent) {
      const jsonObjectMatch = msg.content.match(
        /(\{[\s\S]*?"answer"[\s\S]*?\})/
      );
      if (jsonObjectMatch) {
        parsedContent = tryParse(jsonObjectMatch[1]);
        const jsonStart = msg.content.indexOf(jsonObjectMatch[1]);
        preliminaryText = msg.content.substring(0, jsonStart).trim();
        hasPreliminaryText = preliminaryText.length > 0;
      }
    }

    // 4) Last resort – walk braces to find JSON substring
    if (!parsedContent) {
      const result = extractJsonSubstring(msg.content);
      if (result) {
        parsedContent = tryParse(result.json);
        preliminaryText = msg.content.substring(0, result.start).trim();
        hasPreliminaryText = preliminaryText.length > 0;
      }
    }

    // 5) If still not parsed, attempt regex-based field extraction (handles malformed JSON)
    if (!parsedContent) {
      const sanitize = (text: string) =>
        text
          .replace(/[\u201C\u201D]/g, '"')
          .replace(/\n/g, " ")
          .replace(/\\"/g, '"');

      const safeContent = sanitize(msg.content);

      const captureBetween = (
        startKey: string,
        endKeys: string[]
      ): string | undefined => {
        const startPattern = new RegExp(`"${startKey}"\\s*:\\s*"`, "i");
        const startMatch = safeContent.match(startPattern);
        if (!startMatch) return undefined;
        const startIndex = startMatch.index! + startMatch[0].length;

        // Find nearest end key after start
        let endIndex = safeContent.length;
        for (const k of endKeys) {
          const idx = safeContent.indexOf(`"${k}"`, startIndex);
          if (idx !== -1 && idx < endIndex) endIndex = idx;
        }

        const slice = safeContent.substring(startIndex, endIndex);

        // Remove trailing quotes/comma/spaces
        const cleaned = slice.replace(/"\s*,?\s*$/, "").trim();
        return cleaned;
      };

      const answerFallback = captureBetween("answer", [
        "justification",
        "excerpts_and_references",
        "source_types_used",
        "}",
      ]);
      const justificationFallback = captureBetween("justification", [
        "excerpts_and_references",
        "source_types_used",
        "}",
      ]);

      let referencesFallback: any[] | undefined;
      const refsPattern = /"excerpts_and_references"\s*:\s*(\[[\s\S]*?\])/i;
      const refsMatch = safeContent.match(refsPattern);
      if (refsMatch && refsMatch[1]) {
        try {
          referencesFallback = JSON.parse(refsMatch[1].replace(/\\"/g, '"'));
        } catch {
          /* ignore malformed refs */
        }
      }

      if (answerFallback || justificationFallback || referencesFallback) {
        return {
          ...baseMessage,
          content: preliminaryText || safeContent.split("{")[0].trim(),
          answer: answerFallback,
          justification: justificationFallback,
          references: referencesFallback
            ? referencesFallback.map((ref: any) => ({
              citedText: ref.text_excerpt || ref.citedText || "",
              reference: removeFileExtension(
                ref.source_name || ref.reference || ""
              ),
              pageNumber:
                ref.details?.page_number?.toString() ||
                ref.pageNumber ||
                "N/A",
              type: ref.type || "textbook",
              details: ref.details || undefined,
            }))
            : undefined,
        };
      }
    }
  }

  // If we successfully parsed JSON content, extract structured fields
  if (
    parsedContent &&
    typeof parsedContent === "object" &&
    parsedContent.answer
  ) {
    return {
      ...baseMessage,
      content: hasPreliminaryText ? preliminaryText : "",
      answer: parsedContent.answer || undefined,
      justification: parsedContent.justification || undefined,
      references:
        parsedContent.excerpts_and_references?.map((ref: any) => ({
          citedText: ref.text_excerpt || ref.citedText || "",
          reference: removeFileExtension(
            ref.source_name || ref.reference || ""
          ),
          pageNumber:
            ref.details?.page_number?.toString() || ref.pageNumber || "N/A",
          type: ref.type || "textbook",
          details: ref.details || undefined,
        })) || undefined,
    };
  }

  return baseMessage;
};

/**
 * Chat page component
 *
 * This page displays the chat interface with a sidebar for history,
 * and content area that shows either question or thread content
 * based on user selection or URL parameters.
 */
const Chat = () => {
  // Navigation and route handling
  const { thread_id, question_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [entry] = useState(() => getChatEntry(location.search));

  const {
    bannerHeight,
    handleOverlayClick,
    handleSidebarCollapse,
    handleSidebarToggle,
    isMobileSidebarVisible,
    isSidebarVisible,
    screenSize,
    setBannerHeight,
    setIsMobileSidebarVisible,
  } = useChatLayoutState();

  // Get user from auth context
  const { user, loading: authLoading } = useAuthContext();

  // Close mobile sidebar when item is selected
  const handleMobileItemSelect = (item: ContentItem) => {
    handleItemSelect(item);
    if (screenSize === "mobile") {
      setIsMobileSidebarVisible(false);
    }
  };

  // State to track the selected item from sidebar
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(
    null
  );
  const selectedContentRef = useRef<ContentItem | null>(null);
  const setSelectedContentSynced = (
    value:
      | ContentItem
      | null
      | ((prev: ContentItem | null) => ContentItem | null)
  ) => {
    if (typeof value === "function") {
      setSelectedContent((prev) => {
        const next = value(prev);
        selectedContentRef.current = next;
        return next;
      });
      return;
    }

    selectedContentRef.current = value;
    setSelectedContent(value);
  };
  useEffect(() => {
    selectedContentRef.current = selectedContent;
  }, [selectedContent]);

  // State to trigger sidebar refresh
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Transition state for smooth switching
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  // State for thread messages management
  const [threadMessages, setThreadMessages] = useState<ThreadMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);
  const threadMessagesRequestIdRef = useRef<number>(0);
  // Counter to request ThreadContent to smooth-scroll to bottom
  const [scrollToBottomRequest, setScrollToBottomRequest] = useState<number>(0);

  // State for real-time question data
  const [realTimeQuestionData, setRealTimeQuestionData] =
    useState<QuestionData | null>(null);

  // State to track which field is currently being streamed
  const [currentStreamingField, setCurrentStreamingField] = useState<
    "answer" | "justification" | "references" | null
  >(null);

  // State to track if we're currently processing a message (prevent double submission)
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [activeThreadProcessing, setActiveThreadProcessing] = useState<
    Record<string, ActiveThreadProcessing>
  >({});

  // Ref to hold the current thread messages to avoid stale state in async operations
  const threadMessagesRef = useRef<ThreadMessage[]>([]);
  useEffect(() => {
    threadMessagesRef.current = threadMessages;
  }, [threadMessages]);

  const [reactionPendingMessageIds, setReactionPendingMessageIds] = useState<
    Set<string>
  >(new Set());
  const reactionPendingRef = useRef<Set<string>>(new Set());
  const [reactionErrors, setReactionErrors] = useState<Record<string, string>>(
    {}
  );

  const updateMessageReaction = (
    messageId: string,
    reaction: ChatReaction | null
  ) => {
    const next = threadMessagesRef.current.map((message) =>
      message.message_id === messageId
        ? { ...message, userReaction: reaction }
        : message
    );
    threadMessagesRef.current = next;
    setThreadMessages(next);
  };

  const handleReactionChange = async (
    messageId: string,
    reaction: ChatReactionValue | null,
    reasonCodes: DislikeReasonCode[]
  ): Promise<boolean> => {
    if (reactionPendingRef.current.has(messageId)) return false;

    const currentMessage = threadMessagesRef.current.find(
      (message) => message.message_id === messageId
    );
    if (!currentMessage) return false;
    const previousReaction = currentMessage.userReaction || null;
    const optimisticReaction: ChatReaction | null = reaction
      ? {
          reaction,
          reason_codes: reaction === "dislike" ? reasonCodes : [],
        }
      : null;

    updateMessageReaction(messageId, optimisticReaction);
    reactionPendingRef.current.add(messageId);
    setReactionPendingMessageIds(new Set(reactionPendingRef.current));
    setReactionErrors((current) => {
      const next = { ...current };
      delete next[messageId];
      return next;
    });

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
      const response = await authenticatedFetch(
        `${apiUrl}/api/messages/${messageId}/reaction`,
        reaction
          ? {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                reaction,
                reason_codes: reaction === "dislike" ? reasonCodes : [],
              }),
            }
          : { method: "DELETE" }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Could not save feedback");
      }
      updateMessageReaction(
        messageId,
        normalizeChatReaction(payload.user_reaction)
      );
      return true;
    } catch (error) {
      updateMessageReaction(messageId, previousReaction);
      setReactionErrors((current) => ({
        ...current,
        [messageId]:
          error instanceof Error ? error.message : "Could not save feedback",
      }));
      return false;
    } finally {
      reactionPendingRef.current.delete(messageId);
      setReactionPendingMessageIds(new Set(reactionPendingRef.current));
    }
  };

  const activeThreadProcessingRef = useRef<
    Record<string, ActiveThreadProcessing>
  >({});
  const threadSendReservationsRef = useRef<Set<string>>(new Set());

  // A reader/controller pair belongs to exactly one local request token. Every
  // async continuation checks this ownership before touching UI state.
  const activeStreamRef = useRef<ActiveStream | null>(null);

  // Status polls are keyed by thread + message + generation + local request.
  // Recursive timeouts guarantee there is never more than one request in flight
  // for a given generation.
  const processingPollsRef = useRef<Map<string, ProcessingPoll>>(new Map());

  // A request can outlive the browser's bounded acceptance check. Keep its
  // client-generated UUID so an explicit retry of the exact same prompt is
  // idempotent instead of creating a second generation.
  const [initialUnresolvedGenerationAttempts] = useState(
    loadUnresolvedGenerationAttempts
  );
  const unresolvedGenerationAttemptsRef = useRef(
    initialUnresolvedGenerationAttempts
  );

  const removeUnresolvedGenerationAttempt = (key: string) => {
    unresolvedGenerationAttemptsRef.current.delete(key);
    removePersistedUnresolvedGenerationAttempt(key);
  };

  const clearUnresolvedGenerationById = (
    threadId: string,
    generationId: string
  ) => {
    unresolvedGenerationAttemptsRef.current.forEach((attempt, key) => {
      if (
        attempt.threadId === threadId &&
        attempt.generationId === generationId
      ) {
        removeUnresolvedGenerationAttempt(key);
      }
    });
  };

  const newRequestToken = (kind: "thread" | "question") =>
    `${kind}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const cancelActiveStream = () => {
    const active = activeStreamRef.current;
    if (!active) return;

    // Relinquish ownership first so the aborted read cannot mutate a newer
    // view. Aborting the fetch is sufficient to stop its body reader. Calling
    // reader.cancel() after abort races the browser's BodyStreamBuffer and can
    // reject asynchronously as an uncaught AbortError during thread switches.
    activeStreamRef.current = null;
    active.controller.abort();
  };

  const releaseActiveStream = (requestToken: string) => {
    if (activeStreamRef.current?.requestToken === requestToken) {
      activeStreamRef.current = null;
    }
  };

  const isActiveStreamOwner = (requestToken: string) =>
    activeStreamRef.current?.requestToken === requestToken;

  const stopProcessingPoll = (key: string) => {
    const poll = processingPollsRef.current.get(key);
    if (!poll) return;
    poll.stopped = true;
    if (poll.timer) clearTimeout(poll.timer);
    poll.controller?.abort();
    processingPollsRef.current.delete(key);
  };

  const stopOwnedProcessingPolls = (
    threadId: string,
    requestToken: string,
    generationId?: string
  ) => {
    processingPollsRef.current.forEach((poll, key) => {
      if (
        poll.threadId === threadId &&
        poll.requestToken === requestToken &&
        (!generationId || poll.generationId === generationId)
      ) {
        stopProcessingPoll(key);
      }
    });
  };

  // Cancel any active stream on unmount
  useEffect(() => {
    const polls = processingPollsRef.current;
    return () => {
      cancelActiveStream();
      Array.from(polls.keys()).forEach(stopProcessingPoll);
    };
  }, []);

  // Ref to prevent useEffect from clearing optimistic state during thread creation
  const isCreatingThreadRef = useRef(false);

  // State to track current processing status for display in ThreadContent header
  const [processingStatus, setProcessingStatus] = useState<string>("");

  // State to track which field is currently being streamed for thread messages
  const [currentStreamingThreadField, setCurrentStreamingThreadField] =
    useState<"answer" | "justification" | "references" | null>(null);

  // Accumulate raw JSON chunks synchronously so each SSE chunk can be parsed
  // before React batches state updates.
  const accumulatedThreadJsonRef = useRef<string>("");

  // Agent5: track when the stream has switched to JSON so we suppress raw JSON text
  const isThreadJsonStreamingRef = useRef<boolean>(false);

  // State to accumulate raw JSON chunks for parsing
  const [accumulatedJson, setAccumulatedJson] = useState<string>("");

  // Performance optimization: track field positions to avoid repeated searching
  const [fieldPositions, setFieldPositions] = useState<{
    answerStart?: number;
    justificationStart?: number;
    lastParsedLength?: number;
  }>({});

  // Persist the last used specialty to ensure every turn is sent with a specialty
  const [lastSpecialty, setLastSpecialty] = useState<string>("Endodontics");
  const [lastDomain, setLastDomain] = useState<ChatDomain>(entry.domain || "dentistry");
  const medicineReturn = medicineChatPath(entry.language);

  useEffect(() => {
    const requested = getChatEntry(location.search).domain;
    if (requested && !thread_id && !question_id) setLastDomain(requested);
  }, [location.search, thread_id, question_id]);

  const { credits, creditsLoaded, setSubscriptionPlan, subscriptionPlan } =
    useCreditsSubscription(user?.id, refreshTrigger);

  // Function to convert technical Agent3 messages to user-friendly status
  const getFriendlyStatus = (eventData: any): string => {
    const technicalMessage = eventData.message || "";
    const eventType = eventData.type || "";

    // Map technical messages to user-friendly ones
    const statusMap: { [key: string]: string } = {
      // Workflow events
      conversation_started: "Starting...",
      "Saving your message to conversation": "Thinking...",
      "Loading conversation history": "Recalling context...",
      "Analyzing your question and generating initial response": "Analyzing...",
      "Generating comprehensive response": "Formulating response...",
      "Generating evidence-based response": "Crafting answer...",
      "Conversation processing completed": "",
    };

    // Handle concept-specific events with concept names
    if (eventType === "concepts_identified" && eventData.concepts) {
      const concepts = eventData.concepts;
      if (concepts.length === 1) {
        return `Analyzing "${concepts[0]}"...`;
      } else if (concepts.length === 2) {
        return `Analyzing "${concepts[0]}" and "${concepts[1]}"...`;
      } else if (concepts.length > 2) {
        return `Analyzing "${concepts[0]}", "${concepts[1]}" and ${concepts.length - 2
          } more concepts...`;
      }
    }

    if (eventType === "concept_analysis_initiated" && eventData.concept) {
      return `Researching "${eventData.concept}"...`;
    }

    if (eventType === "knowledge_search_started" && eventData.concept) {
      return `Looking up information on "${eventData.concept}"...`;
    }

    if (
      eventType === "knowledge_search_completed" &&
      eventData.concept &&
      eventData.results_count
    ) {
      return `Found ${eventData.results_count} sources for "${eventData.concept}"...`;
    }

    // Handle dynamic messages (with numbers/variables)
    if (
      technicalMessage.includes("Identified") &&
      technicalMessage.includes("concepts")
    ) {
      return "Analyzing key concepts...";
    }
    if (
      technicalMessage.includes("Beginning analysis of") &&
      technicalMessage.includes("concepts")
    ) {
      return "Researching dental concepts...";
    }
    if (
      technicalMessage.includes("Found") &&
      technicalMessage.includes("sources")
    ) {
      return "Found helpful information...";
    }
    if (
      technicalMessage.includes("Searching") &&
      technicalMessage.includes("knowledge base")
    ) {
      return "Searching for information...";
    }

    // Check for exact matches first
    for (const [key, value] of Object.entries(statusMap)) {
      if (technicalMessage.includes(key)) {
        return value;
      }
    }

    // Event type based mapping
    if (eventType === "concept_analysis_started")
      return "Digging into concepts...";
    if (eventType === "knowledge_search_started")
      return "Searching for information...";
    if (eventType === "content_generation_started")
      return "Crafting response...";
    if (eventType === "excerpts_and_references_started")
      return "Gathering citations...";
    if (eventType === "reference_started" && eventData.reference_index >= 0)
      return `Collecting reference ${eventData.reference_index + 1}...`;
    if (eventType === "conversation_completed") return "";

    // Default fallback for unmatched messages
    if (technicalMessage) {
      return "Thinking...";
    }

    return "";
  };

  const formatProgressSubject = (value?: string) => {
    if (!value || typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    return trimmed.length > 52 ? `${trimmed.slice(0, 49)}...` : trimmed;
  };

  const formatAgentCommentary = (value?: string) => {
    if (!value || typeof value !== "string") return "";
    const cleaned = value
      .replace(/[*_`#>~]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!cleaned) return "";
    return cleaned.length > 600 ? `${cleaned.slice(0, 597)}...` : cleaned;
  };

  const getActivityProgressStep = (
    eventData: any
  ): ActivityProgressStep | null => {
    const eventType = eventData.type || "";
    const concept = formatProgressSubject(eventData.concept);
    const resultsCount =
      typeof eventData.results_count === "number"
        ? eventData.results_count
        : undefined;

    switch (eventType) {
      case "placeholder_created":
      case "conversation_started":
        return { label: "Reading your question" };
      case "history_loading":
      case "history_loaded":
        return { label: "Checking the conversation context" };
      case "response_generation_started":
        return { label: "Framing a focused answer" };
      case "concept_analysis_started":
        return { label: "Identifying what needs evidence" };
      case "concept_analysis_initiated":
        return {
          label: concept
            ? `Checking the evidence for ${concept}`
            : "Checking the evidence",
        };
      case "knowledge_search_started":
        return {
          label: concept
            ? `Searching trusted sources for ${concept}`
            : "Searching trusted sources",
        };
      case "knowledge_search_completed":
        if (resultsCount === 0) {
          return {
            label: concept
              ? `Looking beyond the first source for ${concept}`
              : "Looking beyond the first source",
          };
        }
        return {
          label: concept
            ? `Found ${resultsCount} relevant ${resultsCount === 1 ? "source" : "sources"} for ${concept}`
            : `Found ${resultsCount} relevant ${resultsCount === 1 ? "source" : "sources"}`,
        };
      case "web_search_started":
        return { label: "Checking current literature" };
      case "web_search_completed":
        return { label: "Reviewing current literature" };
      case "answer_started":
      case "content_generation_started":
        return { label: "Drafting the answer" };
      case "justification_started":
        return { label: "Tightening the explanation" };
      case "excerpts_and_references_started":
        return { label: "Organizing the references" };
      case "reference_started":
        return { label: "Adding a supporting reference" };
      default:
        return null;
    }
  };

  const getReasoningSummaryProgressLabel = (summaryText: string) => {
    const cleanLine = (text: string) =>
      text
        .replace(/\[[^\]]+\]\([^)]+\)/g, "$1")
        .replace(/[*_`#>~]/g, "")
        .replace(/\s+/g, " ")
        .trim();

    const lines = summaryText
      .split(/\n+/)
      .map(cleanLine)
      .filter(Boolean);

    let candidate = lines[0] || cleanLine(summaryText);
    if (!candidate) return "";

    const sentenceMatch = candidate.match(/^(.{18,120}?[.!?])\s/);
    if (sentenceMatch?.[1]) {
      candidate = sentenceMatch[1];
    }

    candidate = candidate
      .replace(/^the user (asked|asks|is asking)\s*/i, "Understanding ")
      .replace(/^i('|’)m\s+/i, "")
      .replace(/^i am\s+/i, "")
      .trim();

    if (candidate.length < 12) return "";
    return candidate.length > 96 ? `${candidate.slice(0, 93)}...` : candidate;
  };

  const setThreadProcessingState = (
    threadId: string,
    messageId: string,
    generationId: string,
    requestToken: string,
    status: "pending" | "processing",
    label: string = "Generating response...",
    errorMessage?: string
  ) => {
    const next = {
      ...activeThreadProcessingRef.current,
      [threadId]: {
        messageId,
        generationId,
        requestToken,
        status,
        label,
        errorMessage,
      },
    };
    activeThreadProcessingRef.current = next;
    setActiveThreadProcessing(next);
  };

  const clearThreadProcessingState = (
    threadId: string,
    ownership?: { requestToken: string; generationId?: string }
  ) => {
    const current = activeThreadProcessingRef.current[threadId];
    if (!current) return;
    if (
      ownership &&
      (current.requestToken !== ownership.requestToken ||
        (ownership.generationId &&
          current.generationId !== ownership.generationId))
    ) {
      return;
    }

    const next = { ...activeThreadProcessingRef.current };
    delete next[threadId];
    activeThreadProcessingRef.current = next;
    setActiveThreadProcessing(next);
  };

  const setThreadRecoveryErrorState = (
    threadId: string,
    messageId: string,
    generationId: string,
    requestToken: string,
    errorMessage: string
  ) => {
    const current = activeThreadProcessingRef.current[threadId];
    if (
      !current ||
      current.requestToken !== requestToken ||
      current.generationId !== generationId
    ) {
      return;
    }

    const next = {
      ...activeThreadProcessingRef.current,
      [threadId]: {
        ...current,
        messageId,
        status: "error" as const,
        label: "",
        errorMessage,
      },
    };
    activeThreadProcessingRef.current = next;
    setActiveThreadProcessing(next);
  };

  const isThreadGenerationOwner = (
    threadId: string,
    requestToken: string,
    generationId: string
  ) => {
    const current = activeThreadProcessingRef.current[threadId];
    return (
      current?.requestToken === requestToken &&
      current.generationId === generationId
    );
  };

  const isThreadBusy = (threadId?: string | null) => {
    if (!threadId) return false;
    const status = activeThreadProcessingRef.current[threadId]?.status;
    return status === "pending" || status === "processing";
  };

  // Function to poll for a message that's still processing, even when hidden.
  const pollForProcessingMessage = (
    messageId: string,
    threadId: string,
    label: string = "Generating response...",
    requestToken?: string,
    generationId: string = messageId,
    immediate: boolean = false
  ) => {
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
    const current = activeThreadProcessingRef.current[threadId];
    const ownedRequestToken =
      current?.generationId === generationId
        ? current.requestToken
        : requestToken || `recovery-${threadId}-${generationId}`;
    const key = [threadId, messageId, generationId, ownedRequestToken].join("::");

    // A late fetch/recovery for an older generation must never take ownership
    // away from a newer local request. A single request token may transition
    // from its optimistic ID to the canonical placeholder ID.
    if (
      current &&
      current.requestToken !== ownedRequestToken
    ) {
      return null;
    }

    const existing = processingPollsRef.current.get(key);
    if (existing && !existing.stopped) {
      if (isThreadGenerationOwner(threadId, ownedRequestToken, generationId)) {
        setThreadProcessingState(
          threadId,
          messageId,
          generationId,
          ownedRequestToken,
          "processing",
          label
        );
      }
      return key;
    }

    setThreadProcessingState(
      threadId,
      messageId,
      generationId,
      ownedRequestToken,
      "processing",
      label
    );

    const poll: ProcessingPoll = {
      key,
      threadId,
      messageId,
      generationId,
      requestToken: ownedRequestToken,
      timer: null,
      controller: null,
      stopped: false,
      startedAt: Date.now(),
      unreachableSince: null,
    };
    processingPollsRef.current.set(key, poll);

    const schedule = (delay: number) => {
      if (poll.stopped || processingPollsRef.current.get(key) !== poll) return;
      poll.timer = setTimeout(run, delay);
    };

    const handleUnreachableStatus = (error?: unknown, forceStop = false) => {
      if (poll.stopped || processingPollsRef.current.get(key) !== poll) return;
      const now = Date.now();
      const isFirstFailure = poll.unreachableSince === null;
      const unreachableSince = poll.unreachableSince ?? now;
      if (isFirstFailure) poll.unreachableSince = unreachableSince;

      const matchingStreamIsLive =
        activeStreamRef.current?.requestToken === ownedRequestToken &&
        now - activeStreamRef.current.lastActivityAt < SSE_ACTIVITY_TIMEOUT_MS;
      if (
        !forceStop &&
        (matchingStreamIsLive ||
          now - unreachableSince <
            PROCESSING_STATUS_UNREACHABLE_TIMEOUT_MS)
      ) {
        if (error && isFirstFailure) {
          console.error("Error polling message status:", error);
        }
        schedule(2500);
        return;
      }

      const errorMessage =
        "Unable to verify whether this answer completed. You can try again or reopen the chat.";
      stopProcessingPoll(key);
      setThreadRecoveryErrorState(
        threadId,
        messageId,
        generationId,
        ownedRequestToken,
        errorMessage
      );
      if (activeStreamRef.current?.requestToken === ownedRequestToken) {
        cancelActiveStream();
      }

      if (selectedContentRef.current?.id === threadId) {
        const list = threadMessagesRef.current.map((message) =>
          message.message_id === messageId
            ? {
              ...message,
              processing_status: "error" as const,
              error_message: errorMessage,
            }
            : message
        );
        threadMessagesRef.current = list;
        setThreadMessages(list);
        setProcessingStatus("");
      }

      (window as any).showNotification?.(
        "We could not verify the saved answer. You can try again.",
        "error"
      );
    };

    const run = async () => {
      poll.timer = null;
      if (poll.stopped || processingPollsRef.current.get(key) !== poll) return;
      if (hasProcessingRecoveryExpired({
        startedAt: poll.startedAt,
        now: Date.now(),
        timeoutMs: PROCESSING_STATUS_POLL_TIMEOUT_MS,
      })) {
        handleUnreachableStatus(undefined, true);
        return;
      }
      if (
        !isThreadGenerationOwner(
          threadId,
          ownedRequestToken,
          generationId
        )
      ) {
        stopProcessingPoll(key);
        return;
      }

      const controller = new AbortController();
      poll.controller = controller;
      let attemptTimedOut = false;
      let attemptTimeout: ReturnType<typeof setTimeout> | null = null;
      const timeoutPromise = new Promise<Response>((_, reject) => {
        attemptTimeout = setTimeout(() => {
          attemptTimedOut = true;
          controller.abort();
          reject(new DOMException("Message status timed out", "AbortError"));
        }, PROCESSING_STATUS_REQUEST_TIMEOUT_MS);
      });
      try {
        const res = await Promise.race([
          authenticatedFetch(
            `${apiUrl}/api/messages/${messageId}/status`,
            { signal: controller.signal }
          ),
          timeoutPromise,
        ]);
        if (poll.stopped || processingPollsRef.current.get(key) !== poll) return;
        if (!res.ok) {
          if (res.status === 404) {
            stopProcessingPoll(key);
            const errorMessage =
              "The saved answer could not be recovered. Please try again.";
            setThreadRecoveryErrorState(
              threadId,
              messageId,
              generationId,
              ownedRequestToken,
              errorMessage
            );
            if (activeStreamRef.current?.requestToken === ownedRequestToken) {
              cancelActiveStream();
            }
            if (selectedContentRef.current?.id === threadId) {
              const list = threadMessagesRef.current.map((message) =>
                message.message_id === messageId
                  ? {
                    ...message,
                    processing_status: "error" as const,
                    error_message: errorMessage,
                  }
                  : message
              );
              threadMessagesRef.current = list;
              setThreadMessages(list);
              setProcessingStatus("");
            }
            (window as any).showNotification?.(
              errorMessage,
              "error"
            );
            return;
          }
          handleUnreachableStatus(
            new Error(`Message status request failed with HTTP ${res.status}`)
          );
          return;
        }

        const data = await res.json();
        if (
          poll.stopped ||
          processingPollsRef.current.get(key) !== poll ||
          !isThreadGenerationOwner(
            threadId,
            ownedRequestToken,
            generationId
          )
        ) {
          return;
        }
        if (data.message_id !== messageId) {
          handleUnreachableStatus(
            new Error("Message status did not match the requested generation")
          );
          return;
        }

        const validStatus = ["pending", "processing", "complete", "error"].includes(
          data.processing_status
        );
        if (!validStatus) {
          handleUnreachableStatus(new Error("Invalid message status response"));
          return;
        }
        poll.unreachableSince = null;

        if (data.processing_status === "complete") {
          clearUnresolvedGenerationById(threadId, generationId);
          if (selectedContentRef.current?.id === threadId) {
            const persisted = {
              ...transformMessage(data),
              processing_status: "complete" as const,
              error_message: data.error_message,
            };
            const list = threadMessagesRef.current.map((message) =>
              message.message_id === messageId
                ? {
                  ...message,
                  ...persisted,
                  content: persisted.content || message.content,
                  answer: persisted.answer || message.answer,
                  justification:
                    persisted.justification || message.justification,
                  references: persisted.references || message.references,
                  progressSteps: message.progressSteps,
                }
                : message
            );
            threadMessagesRef.current = list;
            setThreadMessages(list);
          }
          if (activeStreamRef.current?.requestToken === ownedRequestToken) {
            cancelActiveStream();
          }
          stopProcessingPoll(key);
          clearThreadProcessingState(threadId, {
            requestToken: ownedRequestToken,
            generationId,
          });

          // Refresh the visible thread only; hidden threads update when opened.
          if (selectedContentRef.current?.id === threadId) {
            setProcessingStatus("");
            fetchThreadMessages(threadId);
          }
          setRefreshTrigger((prev) => prev + 1);
        } else if (data.processing_status === "error") {
          clearUnresolvedGenerationById(threadId, generationId);
          if (selectedContentRef.current?.id === threadId) {
            const list = threadMessagesRef.current.map((message) =>
              message.message_id === messageId
                ? {
                  ...message,
                  processing_status: "error" as const,
                  error_message:
                    data.error_message || "Answer generation failed.",
                }
                : message
            );
            threadMessagesRef.current = list;
            setThreadMessages(list);
          }
          if (activeStreamRef.current?.requestToken === ownedRequestToken) {
            cancelActiveStream();
          }
          stopProcessingPoll(key);
          clearThreadProcessingState(threadId, {
            requestToken: ownedRequestToken,
            generationId,
          });

          // Show error notification
          (window as any).showNotification?.(
            data.error_message || "An error occurred while generating the response.",
            "error"
          );

          if (selectedContentRef.current?.id === threadId) {
            setProcessingStatus("");
            fetchThreadMessages(threadId);
          }
          setRefreshTrigger((prev) => prev + 1);
        } else {
          if (hasProcessingRecoveryExpired({
            startedAt: poll.startedAt,
            now: Date.now(),
            timeoutMs: PROCESSING_STATUS_POLL_TIMEOUT_MS,
          })) {
            handleUnreachableStatus(undefined, true);
            return;
          }
          // The exact-status endpoint intentionally returns only the answer
          // placeholder. Refresh visible messages as well so durable Agent6
          // tool rows continue to move from running to complete after the
          // original SSE connection was dropped during a thread switch.
          if (selectedContentRef.current?.id === threadId) {
            await fetchThreadMessages(threadId, {
              background: true,
              reconcileProcessing: false,
            });
          }
          if (
            poll.stopped ||
            processingPollsRef.current.get(key) !== poll ||
            !isThreadGenerationOwner(
              threadId,
              ownedRequestToken,
              generationId
            )
          ) {
            return;
          }
          setThreadProcessingState(
            threadId,
            messageId,
            generationId,
            ownedRequestToken,
            data.processing_status === "pending" ? "pending" : "processing",
            "Generating response..."
          );
          schedule(2000);
        }
      } catch (err) {
        if (attemptTimedOut && !poll.stopped) {
          handleUnreachableStatus(
            new Error("Message status request timed out")
          );
        } else if (!controller.signal.aborted && !poll.stopped) {
          handleUnreachableStatus(err);
        }
      } finally {
        if (attemptTimeout !== null) clearTimeout(attemptTimeout);
        if (poll.controller === controller) poll.controller = null;
      }
    };

    schedule(immediate ? 0 : 2000);
    return key;
  };

  // Function to fetch and populate thread messages
  const fetchThreadMessages = async (
    threadId: string,
    options: {
      background?: boolean;
      reconcileProcessing?: boolean;
    } = {}
  ) => {
    if (!threadId) return;

    const requestId = ++threadMessagesRequestIdRef.current;
    const isCurrentThreadRequest = () =>
      threadMessagesRequestIdRef.current === requestId &&
      selectedContentRef.current?.type === "thread" &&
      selectedContentRef.current.id === threadId;

    try {
      if (!options.background) {
        setIsLoadingMessages(true);
      }

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
      const response = await authenticatedFetch(`${apiUrl}/api/messages/${threadId}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Error fetching messages: ${response.statusText}`);
      }

      const messagesData = await response.json();

      if (!isCurrentThreadRequest()) {
        return;
      }

      // Keep retry identity for active rows. If recovery later becomes
      // unavailable, the same prompt must still reuse its original UUID.
      messagesData.forEach((message: any) => {
        if (
          message.role === "assistant" &&
          message.type === "regular" &&
          typeof message.message_id === "string" &&
          ["complete", "error"].includes(
            message.processing_status
          )
        ) {
          clearUnresolvedGenerationById(threadId, message.message_id);
        }
      });

      // Check for any assistant placeholder still processing (user might have disconnected)
      const processingMessage = messagesData.find(
        (msg: any) =>
          msg.role === "assistant" &&
          msg.type === "regular" &&
          (msg.processing_status === "processing" ||
            msg.processing_status === "pending")
      );
      const localRecoveryError = activeThreadProcessingRef.current[threadId];

      // Transform messages using the utility function
      const transformedMessages = messagesData
        .filter(
          (msg: any) =>
            msg.type === "regular" ||
            msg.type === "tool_call" ||
            msg.type === "agent_commentary"
        )
        .map((msg: any) => {
          const transformed = transformMessage(msg);
          const useLocalRecoveryError =
            localRecoveryError?.status === "error" &&
            localRecoveryError.messageId === msg.message_id &&
            isActiveProcessingStatus(msg.processing_status);
          // Preserve processing_status for UI rendering
          return {
            ...transformed,
            processing_status: useLocalRecoveryError
              ? "error"
              : msg.processing_status,
            error_message: useLocalRecoveryError
              ? localRecoveryError.errorMessage
              : msg.error_message,
          };
        });

      // Merge fetched messages with existing ones to avoid overwriting
      setThreadMessages((prev) => {
        if (!isCurrentThreadRequest()) {
          return prev;
        }

        // When we have server messages, remove any temp-user-* messages
        // to prevent duplicates (the server already has the real user message)
        const hasServerUserMessages = transformedMessages.some(
          (m: ThreadMessage) => m.sender === "user" && !m.message_id.startsWith("temp-")
        );
        const hasServerCompleteAssistantMessages = transformedMessages.some(
          (m: ThreadMessage) =>
            m.sender === "ai" &&
            !m.isToolCall &&
            !m.isAgentCommentary &&
            !isActiveProcessingStatus(m.processing_status)
        );
        const serverHasActiveProcessing = Boolean(processingMessage);
        const liveToolActivityByCallId = new Map<string, ThreadMessage>();
        prev.forEach((message) => {
          if (!message.message_id.startsWith("tool-activity-")) return;
          const callId = getToolActivityCallId(message.content);
          if (callId) liveToolActivityByCallId.set(callId, message);
        });
        const persistedToolCallIds = new Set(
          transformedMessages
            .filter((message: ThreadMessage) => message.isToolCall)
            .map((message: ThreadMessage) =>
              getToolActivityCallId(message.content)
            )
            .filter((callId: string | null): callId is string => Boolean(callId))
        );

        // Build a map, but filter out temp messages if we have server messages
        const msgMap: Record<string, ThreadMessage> = {};
        prev.forEach((m) => {
          const liveToolCallId = m.message_id.startsWith("tool-activity-")
            ? getToolActivityCallId(m.content)
            : null;
          // Skip temp-user messages if server has user messages
          if (hasServerUserMessages && m.message_id.startsWith("temp-user-")) {
            return;
          }
          // A persisted tool-call row replaces its live SSE counterpart. They
          // use different message IDs, so reconcile them by provider call ID.
          if (
            liveToolCallId &&
            persistedToolCallIds.has(liveToolCallId)
          ) {
            return;
          }
          // If the server says this thread has no active generation and has a
          // complete assistant message, drop any local optimistic AI shell.
          // This prevents orphaned "Answering" bubbles after the canonical
          // server placeholder has been completed.
          if (
            (serverHasActiveProcessing && m.message_id.startsWith("temp-ai-")) ||
            (hasServerCompleteAssistantMessages &&
              !serverHasActiveProcessing &&
              (m.message_id.startsWith("temp-ai-") ||
                isBlankProcessingAssistant(m)))
          ) {
            return;
          }
          msgMap[m.message_id] = m;
        });

        // Insert / replace with messages just fetched from the server
        transformedMessages.forEach((m: ThreadMessage) => {
          const callId = m.isToolCall
            ? getToolActivityCallId(m.content)
            : null;
          const liveActivity = callId
            ? liveToolActivityByCallId.get(callId)
            : undefined;
          const persistedActivityStatus =
            m.content && typeof m.content === "object"
              ? m.content.status
              : undefined;
          const liveActivityStatus =
            liveActivity?.content &&
              typeof liveActivity.content === "object"
              ? liveActivity.content.status
              : undefined;
          const liveActivityIsNewer =
            persistedActivityStatus === "running" &&
            (liveActivityStatus === "complete" ||
              liveActivityStatus === "error");
          msgMap[m.message_id] =
            liveActivityIsNewer &&
              liveActivity &&
              m.content &&
              typeof m.content === "object" &&
              liveActivity.content &&
              typeof liveActivity.content === "object"
              ? {
                ...m,
                content: {
                  ...m.content,
                  status: liveActivity.content.status,
                  resultsCount: liveActivity.content.resultsCount,
                  errorMessage: liveActivity.content.errorMessage,
                },
                processing_status: liveActivity.processing_status,
              }
              : m;
        });

        // Return messages ordered by timestamp
        let newMessages = Object.values(msgMap).sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        const investigationRowsByGeneration = new Map<
          string,
          ThreadMessage[]
        >();
        const investigationMessageIds = new Set<string>();
        newMessages.forEach((message) => {
          const rowGenerationId = message.isToolCall
            ? message.content?.generationId
            : message.isAgentCommentary
              ? message.generationId
              : undefined;
          if (!rowGenerationId) return;
          const rows =
            investigationRowsByGeneration.get(rowGenerationId) || [];
          rows.push(message);
          investigationRowsByGeneration.set(rowGenerationId, rows);
          investigationMessageIds.add(message.message_id);
        });
        if (investigationMessageIds.size > 0) {
          const reorderedMessages: ThreadMessage[] = [];
          newMessages
            .filter(
              (message) =>
                !investigationMessageIds.has(message.message_id)
            )
            .forEach((message) => {
              const investigationRows =
                investigationRowsByGeneration.get(message.message_id);
              if (investigationRows) {
                reorderedMessages.push(...investigationRows);
                investigationRowsByGeneration.delete(message.message_id);
              }
              reorderedMessages.push(message);
            });
          investigationRowsByGeneration.forEach((rows) => {
            reorderedMessages.push(...rows);
          });
          newMessages = reorderedMessages;
        }
        threadMessagesRef.current = newMessages; // Update ref as well
        return newMessages;
      });

      // If there's a processing message, start polling for completion
      if (processingMessage && options.reconcileProcessing !== false) {
        const current = activeThreadProcessingRef.current[threadId];
        if (
          current?.status === "error" &&
          current.generationId === processingMessage.message_id
        ) {
          return;
        }
        const requestToken =
          current?.generationId === processingMessage.message_id
            ? current.requestToken
            : `recovery-${threadId}-${processingMessage.message_id}`;
        pollForProcessingMessage(
          processingMessage.message_id,
          threadId,
          "Resuming generation...",
          requestToken,
          processingMessage.message_id
        );
      } else if (!processingMessage && options.reconcileProcessing !== false) {
        const current = activeThreadProcessingRef.current[threadId];
        const currentMessage = current
          ? transformedMessages.find(
            (message: ThreadMessage) =>
              message.message_id === current.messageId &&
              !isActiveProcessingStatus(message.processing_status)
          )
          : undefined;
        // A fetch may have started before a newer generation was submitted.
        // Only persisted terminal state for the generation we own may clear it.
        if (current && currentMessage) {
          stopOwnedProcessingPolls(
            threadId,
            current.requestToken,
            current.generationId
          );
          clearThreadProcessingState(threadId, {
            requestToken: current.requestToken,
            generationId: current.generationId,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching thread messages:", error);
      // Preserve the last known/streamed conversation on transient refresh
      // failures. Clearing it here can erase a completed answer while terminal
      // reconciliation is retrying.
    } finally {
      if (isCurrentThreadRequest() && !options.background) {
        setIsLoadingMessages(false);
      }
    }
  };

  // Function to clear thread messages
  const clearThreadMessages = () => {
    threadMessagesRequestIdRef.current += 1;
    setThreadMessages([]);
    threadMessagesRef.current = [];
  };

  // Function to add a message to thread messages
  const addMessageToThread = (message: ThreadMessage) => {
    setThreadMessages((prev) => {
      const next = [...prev, message];
      threadMessagesRef.current = next;
      return next;
    });
  };

  // Get location state to see if we're coming from the homepage with a new question
  const submissionHandledRef = useRef(false);

  // Effect to handle navigation from homepage or login redirect
  useEffect(() => {
    // Wait for auth to finish loading before making any decisions
    if (authLoading) return;

    // Check for QBank discuss data in sessionStorage (from new tab)
    const qbankData = sessionStorage.getItem('qbank_discuss_data');
    let stateData: any = {};
    if (qbankData) {
      try {
        stateData = JSON.parse(qbankData);
      } catch (e) {
        console.error('Failed to parse qbank discuss data:', e);
      }
    }

    // Merge with location.state (location.state takes precedence if both exist)
    const locationState = (location.state as any) || {};
    const mergedState = { ...stateData, ...locationState };
    const newQuestion = mergedState.newQuestion;
    const specialty = mergedState.specialty;
    const hasIncomingDomain =
      mergedState.domain === "dentistry" || mergedState.domain === "medicine";
    const domain: ChatDomain =
      mergedState.domain === "medicine" ? "medicine" : "dentistry";
    const incomingType = mergedState.inputType;

    // Check if there's a new pending submission from the Home page / Login redirect
    // Update last specialty from navigation state when available
    if (specialty && typeof specialty === "string") {
      setLastSpecialty(specialty);
    }
    if (hasIncomingDomain) {
      setLastDomain(domain);
    }

    // Authenticated: resume as before
    if (newQuestion && user && creditsLoaded && !submissionHandledRef.current) {
      // Mark as handled immediately to prevent re-triggering
      submissionHandledRef.current = true;

      // Clear sessionStorage now that we're handling it
      if (qbankData) {
        sessionStorage.removeItem('qbank_discuss_data');
      }

      // Submit the message using the desired mode (thread / question)
      const mode: "thread" | "question" = incomingType || "thread";
      const specialtyToUse = specialty || "Endodontics";
      handleMessageSubmit(newQuestion, mode, specialtyToUse, domain, credits);

      // No need to clear navigation state explicitly; we navigate to the thread URL without state
    }

    // If user is not logged in and trying to submit a message, redirect to login
    if (newQuestion && !user && !submissionHandledRef.current) {
      submissionHandledRef.current = true;
      navigate("/login", {
        state: {
          from: "/chat",
          newQuestion,
          inputType: incomingType || "thread",
          specialty,
          domain,
        },
        replace: true,
      });
    }
  }, [location, user, navigate, creditsLoaded, authLoading]);

  // Effect to handle URL parameters and load thread/question data
  useEffect(() => {
    // When creating a new thread, we handle state optimistically.
    // This ref prevents this effect from wiping that state when the new URL loads.
    if (isCreatingThreadRef.current && thread_id) {
      return;
    }

    if (thread_id) {
      // If content is already loaded for this ID, do nothing.
      // This is key to preventing the wipe of optimistic UI on new thread creation.
      if (selectedContent?.id === thread_id) {
        return;
      }

      // Switched to a thread
      cancelActiveStream();
      setProcessingStatus("");
      // Ensure any messages from a previously viewed thread are cleared BEFORE loading the new thread.
      // Otherwise, the fetched messages will be merged with the old ones (see fetchThreadMessages).
      // This caused messages from different threads to appear together.
      clearThreadMessages();

      if (realTimeQuestionData) {
        setRealTimeQuestionData(null); // Clear question data if we are on a thread
      }
      setSelectedContentSynced({
        id: thread_id,
        title: "Thread " + thread_id.substring(0, 8),
        type: "thread",
        timestamp: new Date().toISOString(),
      });
      fetchThreadMessages(thread_id);
    } else if (question_id) {
      // Switched to a (possibly old) question
      // Clear thread messages because we are in question mode
      clearThreadMessages();
      setIsLoadingMessages(false);

      // If we are no longer looking at the temp/live question that is streaming
      // (or we just switched to a different question), clear the real-time buffer
      // BUT don't clear if we're currently processing and this is just the URL updating
      // with the real question ID (transitioning from temp ID to real ID)
      if (realTimeQuestionData && !isProcessing) {
        setRealTimeQuestionData(null);
      }

      setSelectedContentSynced({
        id: question_id,
        title: "Question " + question_id.substring(0, 8),
        type: "question",
        timestamp: new Date().toISOString(),
      });
    } else {
      // No URL parameters, clear everything
      if (
        isProcessing &&
        activeStreamRef.current?.kind === "question"
      ) {
        return;
      }
      cancelActiveStream();
      setProcessingStatus("");
      setSelectedContentSynced(null);
      clearThreadMessages();
      setIsLoadingMessages(false);
      // Don't clear realTimeQuestionData if we're currently processing a question
      // This prevents clearing the initial status when submitting from fresh /chat page
      if (!isProcessing) {
        setRealTimeQuestionData(null);
      }
    }
  }, [thread_id, question_id, isProcessing]);

  // Handler for when an item is selected from the sidebar
  const handleItemSelect = (item: ContentItem) => {
    // Selecting the already-open item is a no-op. In particular, it must not
    // cancel that item's live stream.
    if (selectedContentRef.current?.id === item.id) {
      return;
    }

    // Cancel any in-flight SSE stream before switching threads/questions
    // Background processing will complete the response even after we disconnect
    cancelActiveStream();

    // Switching views should not clear background progress. Polling continues
    // against accepted assistant placeholders and the next view owns its UI.
    setProcessingStatus("");

    // Always use a transition when switching between content items
    if (selectedContent) {
      setIsTransitioning(true);
      setTimeout(() => {
        updateSelectedContent(item);
        setIsTransitioning(false);
      }, 300); // Sync with CSS animation duration (0.3s)
    } else {
      // No current content, so just set the new one without a transition
      updateSelectedContent(item);
    }
  };

  // Helper function to update selected content and navigate
  const updateSelectedContent = (item: ContentItem) => {
    // Navigation is the only responsibility here.
    // The useEffect hook will handle state updates based on the URL change.
    if (item.type === "thread" && item.id) {
      navigate(`/chat/thread/${item.id}`);
    } else if (item.type === "question" && item.id) {
      navigate(`/chat/question/${item.id}`);
    }
  };

  // Helper function to send message to existing thread
  const sendMessageToThread = async (
    threadId: string,
    message: string,
    specialty: string,
    domain: ChatDomain,
    isNewThread: boolean = false,
    onAccepted?: () => void,
    createThreadWithMessage: boolean = false,
    threadTitle?: string
  ): Promise<boolean> => {
    const generationUserId = user?.id;
    if (!generationUserId) return false;
    if (
      isThreadBusy(threadId) ||
      threadSendReservationsRef.current.has(threadId)
    ) {
      (window as any).showNotification?.(
        "This thread is still generating an answer.",
        "info"
      );
      return false;
    }
    threadSendReservationsRef.current.add(threadId);

    let promptDigest: string;
    try {
      promptDigest = await generationPromptDigest(`${domain}\u0000${message}`);
    } catch (error) {
      threadSendReservationsRef.current.delete(threadId);
      throw error;
    }

    // Invalidate any GET snapshot that started before this generation. It must
    // not merge generation A over the optimistic shell for generation B.
    threadMessagesRequestIdRef.current += 1;
    setIsLoadingMessages(false);

    const isThreadVisible = () =>
      selectedContentRef.current?.type === "thread" &&
      selectedContentRef.current.id === threadId;

    const unresolvedAttemptKey = generationAttemptKey(
      generationUserId,
      threadId,
      promptDigest
    );
    // Re-read the durable key at send time so another browser tab cannot leave
    // this already-mounted tab with a stale in-memory view.
    const unresolvedAttempt =
      unresolvedGenerationAttemptsRef.current.get(unresolvedAttemptKey) ||
      loadUnresolvedGenerationAttempts().get(unresolvedAttemptKey);
    if (unresolvedAttempt) {
      unresolvedGenerationAttemptsRef.current.set(
        unresolvedAttemptKey,
        unresolvedAttempt
      );
    }
    const requestedGenerationId = requestedGenerationIdForAttempt(
      unresolvedAttempt,
      generationUserId,
      threadId,
      promptDigest,
      () => crypto.randomUUID()
    );
    const isRetryingUnresolvedAttempt = Boolean(
      unresolvedAttempt?.generationId === requestedGenerationId
    );
    const requestToken = requestedGenerationId;
    let generationId = requestedGenerationId;
    let canonicalMessageId: string | null = requestedGenerationId;
    let requestDispatched = false;
    let responseReceived = false;
    let requestAccepted = false;
    let acceptanceNotified = false;
    let terminalEventSeen = false;
    let sseWatchdogTimer: ReturnType<typeof setInterval> | null = null;
    let acceptanceTimer: ReturnType<typeof setTimeout> | null = null;
    let acceptanceSignal: AbortSignal | null = null;
    let acceptanceAbortListener: (() => void) | null = null;
    let optimisticUserId: string | null = null;
    let reconcileAfterUnexpectedEnd: (attempt?: number) => Promise<void> =
      async () => { };
    const tempIds: string[] = [];

    const rememberAcceptanceUnknown = () => {
      const attempt: UnresolvedGenerationAttempt = {
        userId: generationUserId,
        threadId,
        promptDigest,
        generationId: requestedGenerationId,
        createdAt: unresolvedAttempt?.createdAt || new Date().toISOString(),
      };
      unresolvedGenerationAttemptsRef.current.set(unresolvedAttemptKey, attempt);
      persistUnresolvedGenerationAttempt(unresolvedAttemptKey, attempt);
    };

    const clearUnresolvedGenerationAttempt = () => {
      const current =
        unresolvedGenerationAttemptsRef.current.get(unresolvedAttemptKey);
      if (current?.generationId === requestedGenerationId) {
        removeUnresolvedGenerationAttempt(unresolvedAttemptKey);
      }
    };

    const stopSseWatchdog = () => {
      if (sseWatchdogTimer !== null) {
        clearInterval(sseWatchdogTimer);
        sseWatchdogTimer = null;
      }
    };

    const stopAcceptanceDeadline = () => {
      if (acceptanceTimer !== null) {
        clearTimeout(acceptanceTimer);
        acceptanceTimer = null;
      }
      if (acceptanceSignal && acceptanceAbortListener) {
        acceptanceSignal.removeEventListener("abort", acceptanceAbortListener);
      }
      acceptanceSignal = null;
      acceptanceAbortListener = null;
    };

    const markRequestAccepted = () => {
      requestAccepted = true;
      if (!acceptanceNotified) {
        acceptanceNotified = true;
        onAccepted?.();
      }
    };

    const startSseWatchdog = () => {
      if (sseWatchdogTimer !== null) return;
      sseWatchdogTimer = setInterval(() => {
        const active = activeStreamRef.current;
        const recoveryMessageId = canonicalMessageId;
        if (!recoveryMessageId || !shouldPollAfterStreamInactivity({
          canonicalMessageId: recoveryMessageId,
          activeRequestToken: active?.requestToken,
          expectedRequestToken: requestToken,
          lastActivityAt: active?.lastActivityAt,
          now: Date.now(),
          inactivityTimeoutMs: SSE_ACTIVITY_TIMEOUT_MS,
        })) {
          return;
        }

        pollForProcessingMessage(
          recoveryMessageId,
          threadId,
          "Reconnecting to the answer...",
          requestToken,
          recoveryMessageId,
          true
        );
      }, 10_000);
    };

    const removeTempMessages = () => {
      if (!tempIds.length) return;
      stopOwnedProcessingPolls(threadId, requestToken);
      clearThreadProcessingState(threadId, { requestToken });
      if (!isThreadVisible()) return;
      setThreadMessages((prev) => {
        const next = prev.filter((m) => !tempIds.includes(m.message_id));
        threadMessagesRef.current = next;
        return next;
      });
    };

    const reconcileAssistantId = (
      fromId: string,
      toId: string,
      status: ThreadMessage["processing_status"] = "processing"
    ) => {
      if (!isThreadVisible()) return null;
      const list = [...threadMessagesRef.current];
      const fromIndex = list.findIndex((item) => item.message_id === fromId);
      const toIndex = list.findIndex((item) => item.message_id === toId);
      let reconciled: ThreadMessage;

      if (toIndex !== -1) {
        const optimistic = fromIndex !== -1 ? list[fromIndex] : null;
        reconciled = {
          ...list[toIndex],
          content: list[toIndex].content || optimistic?.content || "",
          answer: list[toIndex].answer || optimistic?.answer,
          justification:
            list[toIndex].justification || optimistic?.justification,
          references: list[toIndex].references || optimistic?.references,
          progressSteps: list[toIndex].progressSteps || optimistic?.progressSteps,
          processing_status: status,
        };
        list[toIndex] = reconciled;
        if (fromIndex !== -1 && fromIndex !== toIndex) list.splice(fromIndex, 1);
      } else if (fromIndex !== -1) {
        reconciled = {
          ...list[fromIndex],
          message_id: toId,
          processing_status: status,
        };
        list[fromIndex] = reconciled;
      } else {
        reconciled = {
          message_id: toId,
          content: "",
          sender: "ai",
          timestamp: new Date().toISOString(),
          processing_status: status,
        };
        list.push(reconciled);
      }

      threadMessagesRef.current = list;
      setThreadMessages(list);
      return reconciled;
    };

    const recoverRequestedGenerationAcceptance = async (): Promise<boolean> => {
      const deadline = Date.now() + STREAM_ACCEPTANCE_RECOVERY_GRACE_MS;
      const current = activeThreadProcessingRef.current[threadId];
      if (!current || current.requestToken !== requestToken) {
        rememberAcceptanceUnknown();
        return false;
      }

      setThreadProcessingState(
        threadId,
        requestedGenerationId,
        requestedGenerationId,
        requestToken,
        "pending",
        "Checking whether your message was accepted..."
      );

      while (true) {
        const owned = activeThreadProcessingRef.current[threadId];
        if (!owned || owned.requestToken !== requestToken) {
          rememberAcceptanceUnknown();
          return false;
        }
        if (Date.now() >= deadline) {
          rememberAcceptanceUnknown();
          removeTempMessages();
          if (isThreadVisible()) setProcessingStatus("");
          (window as any).showNotification?.(
            "We couldn't confirm that your message was accepted. Please try again.",
            "error"
          );
          return false;
        }

        let responseStatus: number | null = null;
        let statusData: any = null;
        const controller = new AbortController();
        const attemptTimeoutMs = Math.max(
          1,
          Math.min(
            STREAM_ACCEPTANCE_STATUS_TIMEOUT_MS,
            deadline - Date.now()
          )
        );
        let timeout: ReturnType<typeof setTimeout> | null = null;
        const timeoutPromise = new Promise<Response>((_, reject) => {
          timeout = setTimeout(() => {
            controller.abort();
            reject(new DOMException("Generation status timed out", "AbortError"));
          }, attemptTimeoutMs);
        });
        try {
          const apiUrl =
            process.env.REACT_APP_API_URL || "http://localhost:5001";
          const response = await Promise.race([
            authenticatedFetch(
              `${apiUrl}/api/messages/${requestedGenerationId}/status`,
              { signal: controller.signal }
            ),
            timeoutPromise,
          ]);
          responseStatus = response.status;
          if (response.ok) statusData = await response.json();
        } catch {
          responseStatus = null;
        } finally {
          if (timeout !== null) clearTimeout(timeout);
        }

        const stillOwned = activeThreadProcessingRef.current[threadId];
        if (!stillOwned || stillOwned.requestToken !== requestToken) {
          rememberAcceptanceUnknown();
          return false;
        }

        const generationState = statusData
          ? classifyRequestedGeneration(requestedGenerationId, statusData)
          : "invalid";
        if (generationState !== "invalid") {
          markRequestAccepted();
          canonicalMessageId = requestedGenerationId;
          generationId = requestedGenerationId;
          reconcileAssistantId(
            stillOwned.messageId,
            requestedGenerationId,
            statusData.processing_status
          );

          if (generationState === "active") {
            pollForProcessingMessage(
              requestedGenerationId,
              threadId,
              "Reconnecting to the answer...",
              requestToken,
              requestedGenerationId,
              true
            );
          } else {
            clearUnresolvedGenerationById(
              threadId,
              requestedGenerationId
            );
            stopOwnedProcessingPolls(
              threadId,
              requestToken,
              requestedGenerationId
            );
            clearThreadProcessingState(threadId, {
              requestToken,
              generationId: requestedGenerationId,
            });
            if (isThreadVisible()) {
              setProcessingStatus("");
              void fetchThreadMessages(threadId);
            }
            if (statusData.processing_status === "error") {
              (window as any).showNotification?.(
                statusData.error_message || "Answer generation failed.",
                "error"
              );
            }
            setRefreshTrigger((value) => value + 1);
          }
          return true;
        }

        if (
          !shouldRetryGenerationAcceptance({
            responseStatus,
            now: Date.now(),
            deadline,
          })
        ) {
          // A status lookup failure is not proof that the earlier POST was
          // rejected. Preserve the UUID unless the POST itself gave a
          // definitive rejection or this exact row is observed.
          rememberAcceptanceUnknown();
          removeTempMessages();
          if (isThreadVisible()) setProcessingStatus("");
          (window as any).showNotification?.(
            "We couldn't confirm that your message was accepted. Please try again.",
            "error"
          );
          return false;
        }

        const retryDelay = Math.min(1000, Math.max(0, deadline - Date.now()));
        if (retryDelay > 0) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
    };

    reconcileAfterUnexpectedEnd = async (): Promise<void> => {
      const owned = activeThreadProcessingRef.current[threadId];
      if (
        !owned ||
        owned.requestToken !== requestToken ||
        owned.status === "error"
      ) {
        return;
      }

      pollForProcessingMessage(
        requestedGenerationId,
        threadId,
        "Reconnecting to the answer...",
        requestToken,
        requestedGenerationId,
        true
      );
    };

    try {
      setProcessingStatus("Preparing message...");
      accumulatedThreadJsonRef.current = ""; // Reset for new message
      setCurrentStreamingThreadField(null);
      isThreadJsonStreamingRef.current = false;

      // Cancel any previous stream before starting a new one
      cancelActiveStream();
      const streamController = new AbortController();
      activeStreamRef.current = {
        requestToken,
        kind: "thread",
        threadId,
        controller: streamController,
        reader: null,
        lastActivityAt: Date.now(),
      };
      // Add user message immediately, but only if it's not a new thread
      // (because for new threads, the message is added optimistically)
      if (!isNewThread && isThreadVisible()) {
        const existingOptimisticUserId = isRetryingUnresolvedAttempt
          ? [...threadMessagesRef.current]
            .reverse()
            .find(
              (item) =>
                item.sender === "user" &&
                item.message_id.startsWith("temp-user-") &&
                item.content === message
            )?.message_id
          : undefined;
        if (existingOptimisticUserId) {
          optimisticUserId = existingOptimisticUserId;
          tempIds.push(existingOptimisticUserId);
        } else {
          const userMessage: ThreadMessage = {
            message_id: `temp-user-${Date.now()}`,
            content: message,
            sender: "user",
            timestamp: new Date().toISOString(),
          };
          optimisticUserId = userMessage.message_id;
          tempIds.push(userMessage.message_id);
          addMessageToThread(userMessage);
        }
      } else if (isNewThread) {
        optimisticUserId =
          [...threadMessagesRef.current]
            .reverse()
            .find(
              (item) =>
                item.sender === "user" &&
                item.message_id.startsWith("temp-user-") &&
                item.content === message
            )?.message_id || null;
      }

      // Always add a pre-AI placeholder so the UI shows streaming immediately
      const preAiId = requestedGenerationId;
      const preAIMessage: ThreadMessage = {
        message_id: preAiId,
        content: "",
        sender: "ai",
        timestamp: new Date().toISOString(),
        processing_status: "pending",
        progressSteps: [
          {
            id: `progress-${Date.now()}-0`,
            label: "Getting your question ready",
          },
        ],
      };
      tempIds.push(preAiId);
      if (isThreadVisible()) {
        addMessageToThread(preAIMessage);
      }
      setThreadProcessingState(
        threadId,
        preAiId,
        generationId,
        requestToken,
        "pending",
        "Preparing message..."
      );
      // Smooth scroll to the bottom after inserting placeholders
      setScrollToBottomRequest((n) => n + 1);

      // Call the autonomous Agent6 textbook investigator.
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
      // Persist the UUID before dispatch. A response can be accepted even if
      // the browser never receives the first byte of its HTTP response.
      rememberAcceptanceUnknown();
      requestDispatched = true;
      acceptanceSignal = streamController.signal;
      const acceptanceDeadline = new Promise<Response>((_, reject) => {
        acceptanceAbortListener = () => {
          reject(new DOMException("Chat acceptance interrupted", "AbortError"));
        };
        streamController.signal.addEventListener(
          "abort",
          acceptanceAbortListener,
          { once: true }
        );
        acceptanceTimer = setTimeout(() => {
          streamController.abort();
        }, STREAM_ACCEPTANCE_TIMEOUT_MS);
      });
      const chatEndpoint = createThreadWithMessage
        ? "/api/thread/message/agent6"
        : "/api/message/agent6";
      const response = await Promise.race([
        authenticatedFetch(`${apiUrl}${chatEndpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message: message,
            thread_id: threadId,
            user_id: user?.id,
            specialty,
            domain,
            generation_id: requestedGenerationId,
            stream: true,
            ...(createThreadWithMessage ? { title: threadTitle } : {}),
          }),
          signal: streamController.signal,
        }),
        acceptanceDeadline,
      ]);
      stopAcceptanceDeadline();
      responseReceived = true;

      if (response.status === 402) {
        clearUnresolvedGenerationAttempt();
        removeTempMessages();
        (window as any).showNotification?.(
          <>
            You have no credits remaining.{" "}
            <a
              href="/signup"
              style={{
                textDecoration: "underline",
                fontWeight: 600,
                color: "inherit",
              }}
            >
              Create a free account to continue!
            </a>
          </>,
          "error"
        );
        navigate("/signup");
        setProcessingStatus("");
        return false;
      }

      if (response.status === 409) {
        let conflictingGenerationId: string | null = null;
        try {
          const data = await response.json();
          if (data.active_message_id) {
            if (data.active_message_id === requestedGenerationId) {
              markRequestAccepted();
              setThreadProcessingState(
                threadId,
                requestedGenerationId,
                requestedGenerationId,
                requestToken,
                "processing",
                "Generating response..."
              );
              pollForProcessingMessage(
                requestedGenerationId,
                threadId,
                "Generating response...",
                requestToken,
                requestedGenerationId,
                true
              );
              return true;
            }
            conflictingGenerationId = data.active_message_id;
          }
        } catch { }
        if (conflictingGenerationId) {
          // The one-active-generation constraint definitively rejected this
          // UUID. Recover the generation that actually owns the thread.
          clearUnresolvedGenerationAttempt();
          removeTempMessages();
          const recoveryToken = `recovery-${threadId}-${conflictingGenerationId}-${Date.now()}`;
          pollForProcessingMessage(
            conflictingGenerationId,
            threadId,
            "Generating response...",
            recoveryToken,
            conflictingGenerationId,
            true
          );
          (window as any).showNotification?.(
            "This thread is already generating an answer.",
            "info"
          );
          setProcessingStatus("");
          return false;
        }

        // A duplicate/idempotent response with an unreadable body is not proof
        // of rejection. Check only this UUID before deciding whether to retry.
        streamController.abort();
        return await recoverRequestedGenerationAcceptance();
      }

      if (!response.ok) {
        if (response.status === 408 || response.status >= 500) {
          let rejection: any = null;
          try {
            rejection = await response.clone().json();
          } catch { }
          if (
            isDefinitiveGenerationCapacityRejection(
              response.status,
              rejection?.code
            )
          ) {
            clearUnresolvedGenerationAttempt();
            removeTempMessages();
            setProcessingStatus("");
            (window as any).showNotification?.(
              rejection?.error ||
                "The server is busy generating other answers. Please retry shortly.",
              "info"
            );
            return false;
          }
          streamController.abort();
          return await recoverRequestedGenerationAcceptance();
        }
        clearUnresolvedGenerationAttempt();
        removeTempMessages();
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      markRequestAccepted();
      if (activeStreamRef.current?.requestToken === requestToken) {
        activeStreamRef.current.lastActivityAt = Date.now();
      }
      startSseWatchdog();

      // Track if the AI message has been initialized
      let aiMessageInitialized = false;
      let currentAiMessage: ThreadMessage | null = null;
      let reasoningSummaryText = "";
      let lastReasoningSummaryLabel = "";
      const commentaryTextById = new Map<string, string>();
      const contentRevisionBuffer = new StreamingContentRevisionBuffer();
      let pendingThreadRenderFrame: number | null = null;
      let preparePendingThreadRender: (() => void) | null = null;

      // Provider deltas can arrive faster than the display can repaint. Keep
      // the synchronous transcript ref authoritative, but parse and publish at
      // most once per animation frame so Markdown/list rendering does not run
      // once per token.
      const publishThreadMessages = (
        prepare?: () => void,
        immediate = false
      ) => {
        if (prepare) preparePendingThreadRender = prepare;

        const publish = () => {
          pendingThreadRenderFrame = null;
          const pendingPreparation = preparePendingThreadRender;
          preparePendingThreadRender = null;
          pendingPreparation?.();
          if (isThreadVisible()) {
            setThreadMessages([...threadMessagesRef.current]);
          }
        };

        if (immediate) {
          if (pendingThreadRenderFrame !== null) {
            window.cancelAnimationFrame(pendingThreadRenderFrame);
          }
          publish();
          return;
        }
        if (pendingThreadRenderFrame === null) {
          pendingThreadRenderFrame = window.requestAnimationFrame(publish);
        }
      };

      const addActivityProgressStep = (step: ActivityProgressStep | null) => {
        if (!step || !step.label || !isThreadVisible()) return;

        const targetId =
          currentAiMessage?.message_id ||
          canonicalMessageId ||
          preAiId ||
          activeThreadProcessingRef.current[threadId]?.messageId;
        if (!targetId) return;

        const list = [...threadMessagesRef.current];
        const idx = list.findIndex((m) => m.message_id === targetId);
        if (idx === -1) return;

        const existing = list[idx];
        const previousSteps = existing.progressSteps || [];
        const nextKind = step.kind || "status";
        const latestMatchingKind = [...previousSteps]
          .reverse()
          .find((previousStep) => (previousStep.kind || "status") === nextKind);
        if (latestMatchingKind?.label === step.label) return;

        const nextStep = {
          id: `progress-${Date.now()}-${previousSteps.length}`,
          label: step.label,
          kind: nextKind,
        };
        const combinedSteps = [...previousSteps, nextStep];
        const latestCommentary = [...combinedSteps]
          .reverse()
          .find((progressStep) => progressStep.kind === "commentary");
        const recentStatuses = combinedSteps
          .filter((progressStep) => progressStep.kind !== "commentary")
          .slice(-7);
        const retainedSteps = latestCommentary
          ? [...recentStatuses, latestCommentary]
          : recentStatuses.slice(-8);
        const nextMessage = {
          ...existing,
          progressSteps: retainedSteps,
        };
        list[idx] = nextMessage;
        threadMessagesRef.current = list;
        if (currentAiMessage?.message_id === targetId) {
          currentAiMessage = nextMessage;
        }
        setThreadMessages(list);
      };

      const upsertToolActivity = (eventData: Record<string, unknown>) => {
        if (!isThreadVisible()) return;
        const update = toolActivityUpdateFromEvent(
          eventData,
          canonicalMessageId || requestedGenerationId
        );
        if (!update) return;

        const list = [...threadMessagesRef.current];
        const existingIndex = list.findIndex(
          (message) => message.message_id === update.messageId
        );
        const previous =
          existingIndex >= 0 ? list[existingIndex] : undefined;
        const activityMessage: ThreadMessage = {
          ...previous,
          message_id: update.messageId,
          content: update.content,
          sender: "ai",
          timestamp:
            previous?.timestamp ||
            (typeof eventData.timestamp === "string"
              ? eventData.timestamp
              : new Date().toISOString()),
          isToolCall: true,
          processing_status: update.processingStatus,
        };

        if (existingIndex >= 0) {
          list[existingIndex] = activityMessage;
        } else {
          // Keep live tool rows immediately before the answer placeholder.
          // The existing grouping code then renders them inline with the
          // answer and preserves this order when answer streaming begins.
          const answerIndex = currentAiMessage
            ? list.findIndex(
              (message) =>
                message.message_id === currentAiMessage?.message_id
            )
            : -1;
          if (answerIndex >= 0) {
            list.splice(answerIndex, 0, activityMessage);
          } else {
            list.push(activityMessage);
          }
        }

        threadMessagesRef.current = list;
        publishThreadMessages();
      };

      const upsertAgentCommentary = (
        eventData: Record<string, unknown>
      ) => {
        if (!isThreadVisible()) return;
        const isDelta = eventData.type === "agent_commentary_delta";
        const providerCommentaryId = String(
          eventData.commentary_id || "commentary"
        );
        const list = [...threadMessagesRef.current];
        const existingIndex = list.findIndex(
          (message) =>
            message.isAgentCommentary &&
            message.commentaryId === providerCommentaryId
        );
        const previous =
          existingIndex >= 0 ? list[existingIndex] : undefined;
        const rawCommentary = isDelta
          ? `${commentaryTextById.get(providerCommentaryId) || ""}${
            typeof eventData.delta === "string" ? eventData.delta : ""
          }`
          : (eventData.content || eventData.message);
        const commentary = formatAgentCommentary(
          rawCommentary as string | undefined
        );
        if (!commentary) return;
        commentaryTextById.set(
          providerCommentaryId,
          typeof rawCommentary === "string" ? rawCommentary : commentary
        );

        const commentaryMessageId =
          typeof eventData.commentary_message_id === "string"
            ? eventData.commentary_message_id
            : previous?.message_id ||
              `agent-commentary-${
                canonicalMessageId || requestedGenerationId
              }-${providerCommentaryId}`;
        const commentaryMessage: ThreadMessage = {
          ...previous,
          message_id: commentaryMessageId,
          content: commentary,
          sender: "ai",
          timestamp:
            previous?.timestamp ||
            (typeof eventData.timestamp === "string"
              ? eventData.timestamp
              : new Date().toISOString()),
          isAgentCommentary: true,
          commentaryId: providerCommentaryId,
          commentaryRawContent:
            typeof rawCommentary === "string" ? rawCommentary : commentary,
          isCommentaryStreaming: isDelta,
          generationId:
            canonicalMessageId ||
            (typeof eventData.generation_id === "string"
              ? eventData.generation_id
              : requestedGenerationId),
          processing_status: "complete",
        };

        if (existingIndex >= 0) {
          list[existingIndex] = commentaryMessage;
        } else {
          const answerMessageId =
            currentAiMessage?.message_id ||
            canonicalMessageId ||
            requestedGenerationId;
          const answerIndex = list.findIndex(
            (message) => message.message_id === answerMessageId
          );
          if (answerIndex >= 0) {
            list.splice(answerIndex, 0, commentaryMessage);
          } else {
            list.push(commentaryMessage);
          }
        }

        threadMessagesRef.current = list;
        publishThreadMessages();
      };

      // Handle Server-Sent Events for real-time updates (robust parser)
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      const sseParser = new ServerSentEventParser();
      const seenEventSequences = new Set<string>();

      if (reader) {
        // Register as active so navigation/new sends can cancel it
        if (isActiveStreamOwner(requestToken) && activeStreamRef.current) {
          activeStreamRef.current.reader = reader;
        } else {
          await reader.cancel();
        }

        const handleSsePayload = (dataPayload: string) => {
          if (!isActiveStreamOwner(requestToken) || terminalEventSeen) return;
            try {
              const eventData = JSON.parse(dataPayload);
              const eventGenerationId = eventData.generation_id as
                | string
                | undefined;
              if (
                eventGenerationId &&
                canonicalMessageId &&
                eventGenerationId !== canonicalMessageId
              ) {
                return;
              }
              if (eventGenerationId && !canonicalMessageId) {
                canonicalMessageId = eventGenerationId;
                generationId = eventGenerationId;
                currentAiMessage = reconcileAssistantId(
                  currentAiMessage?.message_id || preAiId,
                  eventGenerationId,
                  "processing"
                );
                setThreadProcessingState(
                  threadId,
                  eventGenerationId,
                  eventGenerationId,
                  requestToken,
                  "processing",
                  "Preparing answer..."
                );
              }
              if (
                eventGenerationId &&
                eventData.seq !== undefined &&
                eventData.seq !== null
              ) {
                const sequenceKey = `${eventGenerationId}:${eventData.seq}`;
                if (seenEventSequences.has(sequenceKey)) return;
                seenEventSequences.add(sequenceKey);
              }
              const visibleThread = isThreadVisible();

              // Initialize AI message shell on first relevant event
              if (
                visibleThread &&
                !aiMessageInitialized &&
                ((typeof eventData.type === "string" &&
                  eventData.type.includes("started")) ||
                  eventData.type === "content_chunk")
              ) {
                // Reuse pre-created AI placeholder if present
                const list = [...threadMessagesRef.current];
                const preId = canonicalMessageId || preAiId;
                let reused: ThreadMessage | null = null;
                if (preId) {
                  const idx = list.findIndex((m) => m.message_id === preId);
                  if (idx !== -1) {
                    reused = list[idx];
                  }
                }
                if (reused) {
                  currentAiMessage = reused;
                } else {
                  currentAiMessage = reconcileAssistantId(
                    preAiId,
                    canonicalMessageId || preAiId,
                    canonicalMessageId ? "processing" : "pending"
                  );
                }
                aiMessageInitialized = true;
              }

              // If backend emits a user_message_saved, reconcile optimistic bubble id
              if (
                visibleThread &&
                eventData.type === "user_message_saved" &&
                eventData.message_id
              ) {
                try {
                  const savedId = eventData.message_id as string;
                  const list = threadMessagesRef.current.map((item) =>
                    optimisticUserId && item.message_id === optimisticUserId
                      ? { ...item, message_id: savedId }
                      : item
                  );
                  optimisticUserId = savedId;
                  threadMessagesRef.current = list;
                  setThreadMessages(list);
                } catch { }
              }

              if (
                eventData.type === "placeholder_created" &&
                eventData.message_id
              ) {
                const placeholderId = eventData.message_id as string;
                if (placeholderId !== requestedGenerationId) return;
                canonicalMessageId = placeholderId;
                generationId = placeholderId;
                currentAiMessage = reconcileAssistantId(
                  currentAiMessage?.message_id || preAiId,
                  placeholderId,
                  "processing"
                );
                if (visibleThread) aiMessageInitialized = true;
                setThreadProcessingState(
                  threadId,
                  placeholderId,
                  generationId,
                  requestToken,
                  "processing",
                  "Preparing answer..."
                );
                setRefreshTrigger((prev) => prev + 1);
              }

              if (visibleThread) {
                if (
                  eventData.type === "agent_commentary" ||
                  eventData.type === "agent_commentary_delta"
                ) {
                  upsertAgentCommentary(eventData);
                } else {
                  upsertToolActivity(eventData);
                }
                if (
                  eventData.type !== "agent_commentary" &&
                  eventData.type !== "agent_commentary_delta" &&
                  eventData.type === "reasoning_summary_delta" &&
                  eventData.content
                ) {
                  reasoningSummaryText += eventData.content;
                  const label =
                    getReasoningSummaryProgressLabel(reasoningSummaryText);
                  if (label && label !== lastReasoningSummaryLabel) {
                    lastReasoningSummaryLabel = label;
                    addActivityProgressStep({ label });
                  }
                } else if (
                  eventData.type !== "agent_commentary" &&
                  eventData.type !== "agent_commentary_delta" &&
                  eventData.type === "reasoning_summary_done" &&
                  eventData.content
                ) {
                  const label = getReasoningSummaryProgressLabel(
                    eventData.content
                  );
                  if (label && label !== lastReasoningSummaryLabel) {
                    lastReasoningSummaryLabel = label;
                    addActivityProgressStep({ label });
                  }
                } else if (
                  eventData.type !== "agent_commentary" &&
                  eventData.type !== "agent_commentary_delta"
                ) {
                  addActivityProgressStep(getActivityProgressStep(eventData));
                }
              }

              if (
                eventData.type === "stream_error" ||
                (eventData.type === "error" &&
                  eventData.error_persisted === false)
              ) {
                if (visibleThread) {
                  setProcessingStatus("Reconnecting to the answer...");
                }
                void reconcileAfterUnexpectedEnd();
                (window as any).showNotification?.(
                  "The live connection failed. We are checking the saved answer.",
                  "error"
                );
                return;
              }

              if (
                eventData.type === "conversation_completed" ||
                eventData.type === "error"
              ) {
                // Apply the latest coalesced parse before terminal correction
                // or status changes mutate the canonical in-memory message.
                publishThreadMessages(undefined, true);
                terminalEventSeen = true;
                clearUnresolvedGenerationById(
                  threadId,
                  canonicalMessageId || generationId
                );
                stopOwnedProcessingPolls(
                  threadId,
                  requestToken,
                  canonicalMessageId || generationId
                );
                clearThreadProcessingState(threadId, {
                  requestToken,
                  generationId: canonicalMessageId || generationId,
                });

                if (currentAiMessage) {
                  if (eventData.type === "conversation_completed") {
                    const correctedContent =
                      contentRevisionBuffer.commitRevision();
                    if (correctedContent?.trim()) {
                      currentAiMessage.content = "";
                      delete currentAiMessage.answer;
                      delete currentAiMessage.justification;
                      delete currentAiMessage.references;
                      if (isStructuredThreadTurn(correctedContent)) {
                        parseStreamingThreadJson(
                          correctedContent,
                          currentAiMessage
                        );
                      } else {
                        currentAiMessage.content = correctedContent;
                      }
                    }
                  } else if (contentRevisionBuffer.discardRevision()) {
                    currentAiMessage.content = "";
                    delete currentAiMessage.answer;
                    delete currentAiMessage.justification;
                    delete currentAiMessage.references;
                  }
                  currentAiMessage.processing_status =
                    eventData.type === "error" ? "error" : "complete";
                  currentAiMessage.error_message =
                    eventData.type === "error"
                      ? eventData.message || "Answer generation failed"
                      : undefined;
                }
                if (visibleThread) {
                  setThreadMessages([...threadMessagesRef.current]);
                  setProcessingStatus("");
                  setCurrentStreamingThreadField(null);
                  void fetchThreadMessages(threadId);
                }
                accumulatedThreadJsonRef.current = "";
                isThreadJsonStreamingRef.current = false;
                currentAiMessage = null;
                setRefreshTrigger((value) => value + 1);

                if (eventData.type === "error") {
                  (window as any).showNotification?.(
                    eventData.message ||
                    "An error occurred while generating the response.",
                    "error"
                  );
                }
                return;
              }

              // Update processing status
              const friendlyStatus = getFriendlyStatus(eventData);
              if (friendlyStatus) {
                if (visibleThread) {
                  setProcessingStatus(friendlyStatus);
                }
                const activeInfo = activeThreadProcessingRef.current[threadId];
                if (activeInfo && activeInfo.status !== "error") {
                  setThreadProcessingState(
                    threadId,
                    activeInfo.messageId,
                    activeInfo.generationId,
                    activeInfo.requestToken,
                    activeInfo.status,
                    friendlyStatus
                  );
                }
              }

              if (!visibleThread) {
                return;
              }

              // Handle different event types for real-time updates
              switch (eventData.type) {
                case "web_search_started":
                  break;
                case "content_chunk":
                  if (eventData.section === "initial_response") {
                    if (currentAiMessage) {
                      currentAiMessage.content += eventData.content;
                      publishThreadMessages();
                    }
                  } else if (eventData.section === "final_response") {
                    // Accumulate and parse JSON for structured content.
                    accumulatedThreadJsonRef.current += eventData.content;
                    if (currentAiMessage) {
                      const messageToUpdate = currentAiMessage;
                      publishThreadMessages(
                        () => parseStreamingThreadJson(
                          accumulatedThreadJsonRef.current,
                          messageToUpdate
                        )
                      );
                    }
                  } else if (eventData.section === "turn") {
                    // Agent5/Agent6 stream a single-phase turn. Decide from the
                    // complete accumulated prefix so split JSON tokens never leak
                    // into the plain-text bubble.
                    const chunk = eventData.content || "";
                    const update = contentRevisionBuffer.append(chunk);
                    accumulatedThreadJsonRef.current = update.text;
                    if (!update.shouldPublish) break;

                    const newJson = update.text;
                    const isStructured =
                      isThreadJsonStreamingRef.current ||
                      isStructuredThreadTurn(newJson);

                    if (isStructured) {
                      if (!isThreadJsonStreamingRef.current) {
                        isThreadJsonStreamingRef.current = true;
                        if (currentAiMessage) {
                          currentAiMessage.content = "";
                          setThreadMessages([...threadMessagesRef.current]);
                        }

                        // Ensure the final AI answer bubble appears AFTER tool-call bubbles.
                        // Move the current AI message to the end once JSON streaming begins.
                        if (currentAiMessage) {
                          const list = [...threadMessagesRef.current];
                          const idx = list.findIndex(
                            (m) =>
                              m.message_id === currentAiMessage!.message_id
                          );
                          if (idx !== -1) {
                            const [moved] = list.splice(idx, 1);
                            list.push(moved);
                            threadMessagesRef.current = list;
                            setThreadMessages(list);
                          }
                        }
                      }

                      if (currentAiMessage) {
                        const messageToUpdate = currentAiMessage;
                        publishThreadMessages(
                          () => parseStreamingThreadJson(
                            accumulatedThreadJsonRef.current,
                            messageToUpdate
                          )
                        );
                      }
                    } else if (currentAiMessage) {
                      currentAiMessage.content += chunk;
                      publishThreadMessages();
                    }
                  }
                  break;

                case "content_reset": {
                  const reset = contentRevisionBuffer.reset(eventData.reason);
                  accumulatedThreadJsonRef.current = reset.text;
                  isThreadJsonStreamingRef.current = false;
                  setCurrentStreamingThreadField(null);
                  if (currentAiMessage && reset.shouldClearVisible) {
                    currentAiMessage.content = "";
                    delete currentAiMessage.answer;
                    delete currentAiMessage.justification;
                    delete currentAiMessage.references;
                    setThreadMessages([...threadMessagesRef.current]);
                  }
                  break;
                }

                case "answer_started":
                  setCurrentStreamingThreadField("answer");
                  if (
                    currentAiMessage &&
                    !contentRevisionBuffer.isStagingRevision
                  ) {
                    currentAiMessage.answer = "";
                    setThreadMessages([...threadMessagesRef.current]);
                  }
                  break;

                case "justification_started":
                  setCurrentStreamingThreadField("justification");
                  if (
                    currentAiMessage &&
                    !contentRevisionBuffer.isStagingRevision
                  ) {
                    currentAiMessage.justification = "";
                    setThreadMessages([...threadMessagesRef.current]);
                  }
                  break;

                case "excerpts_and_references_started":
                  setCurrentStreamingThreadField("references");
                  if (
                    currentAiMessage &&
                    !contentRevisionBuffer.isStagingRevision
                  ) {
                    currentAiMessage.references = [];
                    setThreadMessages([...threadMessagesRef.current]);
                  }
                  break;

                case "message_saved":
                  // Persistence acknowledgement only. A generation remains
                  // active until conversation_completed or error.
                  break;

                case "concept_analysis_initiated":
                  break;
              }
          } catch (e) {
            // A malformed record must not make the transport look terminal.
            // We continue reading and reconcile persisted status at EOF if no
            // owned terminal event ever arrives.
            console.error("Error parsing thread SSE data:", e);
          }
        };

        while (isActiveStreamOwner(requestToken)) {
          const { done, value } = await reader.read();
          if (done) break;
          const events = sseParser.feed(decoder.decode(value, { stream: true }));
          // SSE comments are transport heartbeats, not model progress. Only a
          // parsed data event postpones recovery or stops an existing poll.
          if (
            events.length > 0 &&
            activeStreamRef.current?.requestToken === requestToken
          ) {
            activeStreamRef.current.lastActivityAt = Date.now();
            if (canonicalMessageId) {
              stopOwnedProcessingPolls(
                threadId,
                requestToken,
                canonicalMessageId
              );
            }
          }
          events.forEach((event) => handleSsePayload(event.data));
        }

        const trailingEvents = sseParser.finish(decoder.decode());
        trailingEvents.forEach((event) => handleSsePayload(event.data));
      } else {
        console.error("Streaming response did not include a response body");
      }

      releaseActiveStream(requestToken);
      if (!terminalEventSeen) {
        void reconcileAfterUnexpectedEnd();
      }

      // Refresh sidebar to show updated thread timestamp
      setRefreshTrigger((prev) => prev + 1);
      // Final state update on success
      if (isThreadVisible()) {
        publishThreadMessages(undefined, true);
      }
      return true;
    } catch (error) {
      stopAcceptanceDeadline();
      const wasDetached =
        !isActiveStreamOwner(requestToken) ||
        (error instanceof DOMException && error.name === "AbortError");
      if (!wasDetached) {
        console.error("Error sending message to thread:", error);
      }

      if (requestAccepted) {
        void reconcileAfterUnexpectedEnd();
        setRefreshTrigger((prev) => prev + 1);
        return true;
      }

      if (requestDispatched && !responseReceived) {
        const recovered = await recoverRequestedGenerationAcceptance();
        setRefreshTrigger((prev) => prev + 1);
        return recovered;
      }

      if (isThreadVisible()) {
        removeTempMessages();
      } else {
        clearThreadProcessingState(threadId, { requestToken });
        setRefreshTrigger((prev) => prev + 1);
      }
      return false;
    } finally {
      threadSendReservationsRef.current.delete(threadId);
      stopAcceptanceDeadline();
      stopSseWatchdog();
      releaseActiveStream(requestToken);
      if (isThreadVisible() && (terminalEventSeen || !requestAccepted)) {
        setProcessingStatus("");
      }
    }
  };

  // Helper function to create new thread with message
  const createNewThreadWithMessage = async (
    message: string,
    specialty: string,
    domain: ChatDomain,
    onAccepted?: () => void
  ): Promise<boolean> => {
    try {
      isCreatingThreadRef.current = true;
      setProcessingStatus("Creating thread...");
      const threadId = crypto.randomUUID();
      const threadTitle =
        message.substring(0, 50) + (message.length > 50 ? "..." : "");

      // --- Optimistic UI Update ---
      // The browser owns the final UUID, so the optimistic thread can keep the
      // same identity through acceptance and interrupted-request retries.
      const userMessage: ThreadMessage = {
        message_id: `temp-user-${Date.now()}`,
        content: message,
        sender: "user",
        timestamp: new Date().toISOString(),
      };
      const tempThread: ContentItem = {
        id: threadId,
        title: threadTitle,
        type: "thread" as const,
        timestamp: new Date().toISOString(),
      };

      setSelectedContentSynced(tempThread);
      threadMessagesRef.current = [userMessage];
      setThreadMessages([userMessage]); // Show the new message right away
      setScrollToBottomRequest((n) => n + 1);
      navigate(`/chat/thread/${threadId}`, { replace: true });

      // One request now creates the thread and accepts its first Agent6
      // generation, removing a full authenticated API round trip.
      const accepted = await sendMessageToThread(
        threadId,
        message,
        specialty,
        domain,
        true,
        onAccepted,
        true,
        threadTitle
      );
      setRefreshTrigger((prev) => prev + 1);
      if (!accepted) {
        // The combined endpoint creates the caller-addressed thread before it
        // checks generation capacity. Keep it open so definitive rejections can
        // be retried without creating duplicate chats.
        const promptDigest = await generationPromptDigest(`${domain}\u0000${message}`);
        if (
          user?.id &&
          unresolvedGenerationAttemptsRef.current.has(
            generationAttemptKey(user.id, threadId, promptDigest)
          )
        ) {
          return false;
        }
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error creating new thread:", error);
      setRefreshTrigger((prev) => prev + 1);
      (window as any).showNotification?.(
        "We couldn't confirm the new chat. Please try the message again.",
        "error"
      );
      return false;
    } finally {
      isCreatingThreadRef.current = false;
      setProcessingStatus("");
    }
  };

  // Handle message submission from InputBar
  const handleMessageSubmit = async (
    message: string,
    inputType: "thread" | "question",
    specialty: string,
    domain: ChatDomain,
    currentCredits: number,
    onAccepted?: () => void
  ): Promise<boolean> => {
    // Require login for all interactions
    if (!user) {
      (window as any).showNotification?.(
        <>
          Please log in to use the chat.{" "}
          <a
            href="/login"
            style={{
              textDecoration: "underline",
              fontWeight: 600,
              color: "inherit",
            }}
          >
            Log in now
          </a>
        </>,
        "info"
      );
      navigate("/login", { state: {
        from: domain === "medicine" ? medicineReturn : "/chat",
        newQuestion: message,
        inputType,
        specialty,
        domain,
      } });
      return false;
    }

    // Front-end guard: block only if user has no active subscription AND no credits
    // If subscription state hasn't loaded yet, do a quick backend check to avoid false blocks
    if (currentCredits <= 0) {
      let hasActiveSubscription = !!subscriptionPlan;
      if (!hasActiveSubscription && user?.id) {
        try {
          const apiUrl =
            process.env.REACT_APP_API_URL || "http://localhost:5001";
          const data = await runWithAbortTimeout(
            CHAT_CONTROL_REQUEST_TIMEOUT_MS,
            async (signal) => {
              const res = await authenticatedFetch(
                `${apiUrl}/api/subscriptions/${user.id}`,
                { signal }
              );
              return res.ok ? await res.json() : null;
            },
            "Subscription check timed out"
          );
          if (data && data.status === "active") {
            hasActiveSubscription = true;
            setSubscriptionPlan(data.plan || "premium");
          }
        } catch (e) {
          // Network errors shouldn't block subscribers later; fall through to existing guard
        }
      }
      if (!hasActiveSubscription) {
        (window as any).showNotification?.(
          <>
            You have no credits remaining.{" "}
            <a
              href="/pricing"
              style={{
                textDecoration: "underline",
                fontWeight: 600,
                color: "inherit",
              }}
            >
              Click here to get unlimited credits!
            </a>
          </>,
          "error"
        );
        navigate("/pricing");
        return false;
      }
    }

    // Require authentication and specialty for direct questions
    if (inputType === "question" && (!user?.id || !specialty)) {
      console.error("User not authenticated or specialty not selected");
      return false;
    }

    if (inputType === "thread") {
      if (selectedContent?.type === "thread" && selectedContent.id) {
        const usedSpecialty =
          specialty && specialty.trim() ? specialty : lastSpecialty;
        const usedDomain = domain || lastDomain;
        // Remember the last specialty for subsequent turns
        if (specialty && specialty.trim()) setLastSpecialty(specialty);
        setLastDomain(usedDomain);
        return await sendMessageToThread(
          selectedContent.id,
          message,
          usedSpecialty,
          usedDomain,
          false,
          onAccepted
        );
      } else {
        const usedSpecialty =
          specialty && specialty.trim() ? specialty : lastSpecialty;
        const usedDomain = domain || lastDomain;
        if (specialty && specialty.trim()) setLastSpecialty(specialty);
        setLastDomain(usedDomain);
        return await createNewThreadWithMessage(
          message,
          usedSpecialty,
          usedDomain,
          onAccepted
        );
      }
    } else if (inputType === "question") {
      if (isProcessing) {
        return false;
      }

      const requestToken = newRequestToken("question");
      const streamController = new AbortController();
      let terminalEventSeen = false;
      let persistedQuestionId: string | undefined;
      try {
        setIsProcessing(true);
        cancelActiveStream();
        activeStreamRef.current = {
          requestToken,
          kind: "question",
          controller: streamController,
          reader: null,
          lastActivityAt: Date.now(),
        };
        // Create temporary question for immediate UI feedback
        const tempQuestionId = "temp-" + Date.now();
        const tempQuestion: ContentItem = {
          id: tempQuestionId,
          title: message.substring(0, 30) + (message.length > 30 ? "..." : ""),
          type: "question",
          timestamp: new Date().toISOString(),
        };

        // Initialize before the request starts. The first SSE event can arrive
        // before the visual transition ends and must not be erased by its timer.
        initializeQuestionData(message, specialty);

        // Update UI with transition
        if (selectedContent) {
          setIsTransitioning(true);
          setTimeout(() => {
            if (!isActiveStreamOwner(requestToken)) {
              setIsTransitioning(false);
              return;
            }
            if (persistedQuestionId) {
              setIsTransitioning(false);
              return;
            }
            setSelectedContentSynced(tempQuestion);
            setIsTransitioning(false);
          }, 200);
        } else {
          setSelectedContentSynced(tempQuestion);
        }

        // Call Questions2 API
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
        // Use Questions4 endpoint (o4-mini)
        const response = await authenticatedFetch(`${apiUrl}/api/question/v4`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question: message,
            specialty: specialty,
            user_id: user!.id,
            stream: true,
          }),
          signal: streamController.signal,
        });

        if (response.status === 402) {
          (window as any).showNotification?.(
            <>
              You have no credits remaining.{" "}
              <a
                href="/pricing"
                style={{
                  textDecoration: "underline",
                  fontWeight: 600,
                  color: "inherit",
                }}
              >
                Click here to get unlimited credits!
              </a>
            </>,
            "error"
          );
          navigate("/pricing");
          setIsProcessing(false);
          return false;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        onAccepted?.();

        // Direct questions use the same incremental parser and stream ownership
        // as thread chat, so arbitrary transport chunking cannot lose records.
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        const sseParser = new ServerSentEventParser();

        if (reader) {
          if (isActiveStreamOwner(requestToken) && activeStreamRef.current) {
            activeStreamRef.current.reader = reader;
          } else {
            await reader.cancel();
          }

          const handleQuestionPayload = (payload: string) => {
            if (!isActiveStreamOwner(requestToken)) return;
            try {
              const eventData = JSON.parse(payload);
              handleRealTimeEvent(eventData);

              if (
                eventData.type === "process_completed" ||
                eventData.type === "error"
              ) {
                terminalEventSeen = true;
              }

              // Update URL with real question ID
              if (
                eventData.type === "database_saved" &&
                eventData.question_id
              ) {
                const savedQuestionId = eventData.question_id as string;
                persistedQuestionId = savedQuestionId;
                navigate(`/chat/question/${savedQuestionId}`);
                setRefreshTrigger((prev) => prev + 1);

                setTimeout(() => {
                  if (!isActiveStreamOwner(requestToken)) return;
                  setSelectedContentSynced((prev) =>
                    prev ? { ...prev, id: savedQuestionId } : null
                  );
                }, 150);
              }
            } catch (error) {
              console.error("Error parsing question SSE data:", error);
            }
          };

          while (isActiveStreamOwner(requestToken)) {
            const { done, value } = await reader.read();
            if (done) break;
            if (activeStreamRef.current?.requestToken === requestToken) {
              activeStreamRef.current.lastActivityAt = Date.now();
            }
            sseParser
              .feed(decoder.decode(value, { stream: true }))
              .forEach((event) => handleQuestionPayload(event.data));
          }
          sseParser
            .finish(decoder.decode())
            .forEach((event) => handleQuestionPayload(event.data));
        }

        if (!terminalEventSeen && isActiveStreamOwner(requestToken)) {
          setRealTimeQuestionData((prev) => ({
            ...(prev || {}),
            status:
              "The live answer ended unexpectedly. Please reopen this question or try again.",
          }));
          (window as any).showNotification?.(
            "The question stream ended before completion.",
            "error"
          );
        }
      } catch (error) {
        const wasDetached =
          !isActiveStreamOwner(requestToken) ||
          (error instanceof DOMException && error.name === "AbortError");
        if (!wasDetached) console.error("Error creating question:", error);
        return false;
      } finally {
        releaseActiveStream(requestToken);
        setIsProcessing(false);
      }
    }
    return true;
  };

  // Initialize question data for real-time updates
  const initializeQuestionData = (message: string, specialty: string) => {
    setRealTimeQuestionData({
      questionText: message,
      specialty: specialty,
      status: "Preparing your question...",
      answer: "",
      justification: "",
      references: [],
    });
    setCurrentStreamingField(null);
    setAccumulatedJson("");
    setFieldPositions({});
  };

  // Function to handle real-time SSE events and update question data
  const handleRealTimeEvent = (eventData: any) => {
    switch (eventData.type) {
      case "process_started":
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: "Thinking about your question...",
        }));
        break;

      case "database_saving":
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: "Taking note of your question...",
        }));
        break;

      case "database_saved":
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: "All set. Searching for answers...",
        }));
        break;

      case "retrieval_started":
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: eventData.concept
            ? `Looking up information on "${eventData.concept}"...`
            : `Exploring reliable sources...`,
        }));
        break;

      case "retrieval_completed":
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: eventData.concept
            ? `Found ${eventData.results_count || 0} insights on "${eventData.concept
            }"`
            : `Found ${eventData.results_count || 0} helpful references`,
        }));
        break;

      case "concepts_identified":
        if (eventData.concepts && eventData.concepts.length > 0) {
          const concepts = eventData.concepts as string[];
          let msg = "Analyzing ";
          if (concepts.length === 1) {
            msg += `"${concepts[0]}"...`;
          } else if (concepts.length === 2) {
            msg += `"${concepts[0]}" and "${concepts[1]}"...`;
          } else {
            msg += `"${concepts[0]}", "${concepts[1]}" and ${concepts.length - 2
              } more concepts...`;
          }
          setRealTimeQuestionData((prev) => ({
            ...(prev || {}),
            status: msg,
          }));
        }
        break;

      case "response_generation_started":
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: "Crafting your answer...",
        }));
        break;

      case "answer_started":
        setCurrentStreamingField("answer");
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: "Composing answer...",
          answer: "",
        }));
        break;

      case "justification_started":
        setCurrentStreamingField("justification");
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: "Explaining reasoning...",
          justification: "",
        }));
        break;

      case "excerpts_and_references_started":
        setCurrentStreamingField("references");
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: "Gathering citations...",
        }));
        break;

      case "content_chunk":
        if (eventData.content) {
          setAccumulatedJson((prev) => {
            const newJson = prev + eventData.content;
            parseStreamingJson(newJson);
            return newJson;
          });
        }
        break;

      case "process_completed":
        setCurrentStreamingField(null);
        setAccumulatedJson("");
        setFieldPositions({});
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: "",
        }));
        break;

      case "error":
        setCurrentStreamingField(null);
        setAccumulatedJson("");
        setFieldPositions({});
        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          status: `Error: ${eventData.message || "Unknown error occurred"}`,
        }));
        console.error("SSE Error:", eventData);
        break;

      default:
        // Unhandled event type - no action needed
        break;
    }
  };

  // Function to parse streaming JSON for thread messages and update state
  const parseStreamingThreadJson = (
    jsonString: string,
    messageToUpdate: ThreadMessage
  ) => {
    const answerContent = extractStreamingJsonString(jsonString, "answer");
    if (answerContent !== undefined) {
      messageToUpdate.answer = answerContent;
    }

    const justificationContent = extractStreamingJsonString(
      jsonString,
      "justification"
    );
    if (justificationContent !== undefined) {
      messageToUpdate.justification = justificationContent;
    }

    const mapReference = (ref: any) => ({
      citedText: ref.text_excerpt || ref.citedText || "",
      reference: removeFileExtension(
        ref.source_name || ref.reference || ""
      ),
      pageNumber:
        ref.details?.page_number?.toString() || ref.pageNumber || "N/A",
      type: ref.type || "textbook",
      details: ref.details || undefined,
    });

    const parsedReferences = extractStreamingJsonArrayObjects(
      jsonString,
      "excerpts_and_references"
    );
    if (parsedReferences !== undefined) {
      messageToUpdate.references = parsedReferences.filter(isDisplayableReference).map(mapReference);
    }
  };

  // Optimized function to parse accumulated JSON and extract field content
  const parseStreamingJson = (jsonString: string) => {
    const currentLength = jsonString.length;

    // Skip parsing if string hasn't grown enough (performance optimization)
    if (
      fieldPositions.lastParsedLength &&
      currentLength - fieldPositions.lastParsedLength < 10
    ) {
      return false;
    }

    // Try complete JSON parse only if string looks complete
    if (jsonString.trimEnd().endsWith("}")) {
      try {
        const parsed = JSON.parse(jsonString);

        setRealTimeQuestionData((prev) => ({
          ...(prev || {}),
          answer: parsed.answer || prev?.answer || "",
          justification: parsed.justification || prev?.justification || "",
          references: parsed.excerpts_and_references
            ? parsed.excerpts_and_references.map((ref: any) => ({
              citedText: ref.text_excerpt,
              reference: removeFileExtension(ref.source_name),
              pageNumber: ref.details?.page_number?.toString() || "N/A",
              type: ref.type || "textbook",
              details: ref.details || undefined,
            }))
            : prev?.references || [],
        }));

        return true;
      } catch (e) {
        // Fall through to progressive parsing
      }
    }

    // Progressive parsing with position tracking
    let answerContent = "";
    let justificationContent = "";
    let newFieldPositions = { ...fieldPositions };

    // Find answer field position
    if (!newFieldPositions.answerStart) {
      const answerMatch = jsonString.match(/"answer":\s*"/);
      if (answerMatch) {
        newFieldPositions.answerStart =
          answerMatch.index! + answerMatch[0].length;
      }
    }

    // Extract answer content efficiently
    if (newFieldPositions.answerStart) {
      let i = newFieldPositions.answerStart;
      while (i < currentLength) {
        const char = jsonString[i];
        if (char === '"' && jsonString[i - 1] !== "\\") break;
        answerContent += char;
        i++;
      }
      answerContent = answerContent.replace(/\\"/g, '"').replace(/\\n/g, "\n");
    }

    // Find justification field position
    if (!newFieldPositions.justificationStart) {
      const justificationMatch = jsonString.match(/"justification":\s*"/);
      if (justificationMatch) {
        newFieldPositions.justificationStart =
          justificationMatch.index! + justificationMatch[0].length;
      }
    }

    // Extract justification content efficiently
    if (newFieldPositions.justificationStart) {
      let i = newFieldPositions.justificationStart;
      while (i < currentLength) {
        const char = jsonString[i];
        if (char === '"' && jsonString[i - 1] !== "\\") break;
        justificationContent += char;
        i++;
      }
      justificationContent = justificationContent
        .replace(/\\"/g, '"')
        .replace(/\\n/g, "\n");
    }

    // Update field positions
    newFieldPositions.lastParsedLength = currentLength;
    setFieldPositions(newFieldPositions);

    // Update data if content changed
    if (answerContent || justificationContent) {
      setRealTimeQuestionData((prev) => ({
        ...(prev || {}),
        ...(answerContent && { answer: answerContent }),
        ...(justificationContent && { justification: justificationContent }),
      }));
    }

    return false;
  };

  // Determine CSS classes based on screen size and sidebar state
  const getHeaderClass = () => {
    if (screenSize === "mobile" || !user) return styles.headerFullWidth;
    if (!isSidebarVisible) return styles.headerHidden;
    return styles.headerExpanded;
  };

  const getContentAreaClass = () => {
    if (screenSize === "mobile" || !user) return styles.contentAreaFullWidth;
    if (!isSidebarVisible) return styles.contentAreaHidden;
    return styles.contentAreaExpanded;
  };

  const getInputBarAreaClass = () => {
    if (screenSize === "mobile" || !user) return styles.inputBarAreaFullWidth;
    if (!isSidebarVisible) return styles.inputBarAreaHidden;
    return styles.inputBarAreaExpanded;
  };

  // Callback to handle banner height changes
  const handleBannerHeightChange = (height: number) => {
    setBannerHeight(height);
  };

  const selectedThreadProcessing =
    selectedContent?.type === "thread" && selectedContent.id
      ? activeThreadProcessing[selectedContent.id]
      : undefined;
  const selectedThreadBusy =
    selectedThreadProcessing?.status === "pending" ||
    selectedThreadProcessing?.status === "processing";
  const visibleProcessingStatus =
    selectedThreadBusy && selectedThreadProcessing
      ? selectedThreadProcessing.label
      : processingStatus;
  const inputIsProcessing =
    selectedThreadBusy || (selectedContent?.type !== "thread" && isProcessing);

  return (
    <div className={styles.chatPageContainer}>
      <SEO
        title={lastDomain === "medicine" ? "C-Shaped | Medicine AI Study Assistant" : "C-Shaped | Chat"}
        description={lastDomain === "medicine" ? "Review medical concepts for SMLE with textbook-based explanations and follow-up questions." : "Chat with our AI dental assistant for in-depth, reference-backed answers."}
        canonical="https://c-shaped.com/chat"
        ogTitle="Chat with C-Shaped AI"
        ogImage="https://c-shaped.com/logo.png"
      />
      {/* Announcement Banner - positioned at the very top */}
      <div style={{ position: "relative", zIndex: 1001 }}>
        <AnnouncementBanner
          location="global"
          userId={user?.id}
          onHeightChange={handleBannerHeightChange}
        />
      </div>
      <div
        className={styles.chatContainer}
        style={
          {
            "--banner-height": `${bannerHeight}px`,
            paddingTop: bannerHeight,
          } as React.CSSProperties & { "--banner-height": string }
        }
      >
        {/* Sidebar overlay for mobile */}
        {screenSize === "mobile" && (
          <div
            className={`${styles.sidebarOverlay} ${isMobileSidebarVisible ? styles.active : ""
              }`}
            onClick={handleOverlayClick}
          />
        )}

        {/* Header with responsive design */}
        <div className={`${styles.header} ${getHeaderClass()}`}>
          <div className={styles.headerLogoContainer}>
            {/* Sidebar toggle button - show when sidebar is hidden or on mobile */}
            {((screenSize !== "mobile" && !isSidebarVisible) ||
              (screenSize === "mobile" && !isMobileSidebarVisible)) &&
              user && (
                <button
                  className={styles.sidebarToggleHeader}
                  onClick={handleSidebarToggle}
                  aria-label={screenSize === "mobile" ? "Open sidebar" : "Show sidebar"}
                >
                  <Bars3BottomLeftIcon className={styles.sidebarToggleIcon} />
                </button>
              )}

            {/* Header logo - Modified logic for mobile */}
            {(screenSize !== "mobile" ||
              (screenSize === "mobile" && !isMobileSidebarVisible)) && (
                <div className={styles.headerLogo}>
                  <Link to="/">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="96"
                      height="52"
                      viewBox="0 0 96 52"
                      fill="none"
                    >
                      <path
                        d="M4.33978 33.7068C4.48592 35.1368 4.84051 36.1443 5.40357 36.7293C5.96663 37.297 6.77683 37.5245 7.83418 37.4118C8.80986 37.3121 9.71032 36.9915 10.5356 36.4498C11.378 35.8908 12.055 35.1736 12.5664 34.2983L13.1854 34.4933C12.7728 36.0186 11.911 37.2948 10.6 38.3218C9.30629 39.3315 7.89865 39.9165 6.3771 40.0768C4.39136 40.2891 2.90205 39.9425 1.90917 39.0368C0.933495 38.1095 0.329605 36.5343 0.0975044 34.3113C-0.0529309 32.8813 -0.0292911 31.3711 0.168424 29.7808C0.366139 28.1905 0.716438 26.7518 1.21932 25.4648C1.87694 23.8051 2.74517 22.5095 3.824 21.5778C4.92003 20.6245 6.19443 20.072 7.64721 19.9203C9.01402 19.773 10.0972 19.9636 10.8966 20.4923C11.709 20.995 12.1732 21.8075 12.2892 22.9298C12.3709 23.7055 12.2785 24.3165 12.012 24.7628C11.7455 25.1918 11.3221 25.4366 10.7419 25.4973C10.4496 25.5276 10.1702 25.506 9.90374 25.4323C9.65444 25.337 9.42234 25.1875 9.20744 24.9838C9.49971 24.7931 9.74256 24.453 9.93597 23.9633C10.1251 23.4563 10.1939 22.9601 10.1423 22.4748C10.0907 21.9765 9.91448 21.6038 9.61361 21.3568C9.30844 21.1098 8.90871 21.0123 8.41443 21.0643C7.66225 21.1466 6.96595 21.6905 6.32552 22.6958C5.6851 23.6881 5.17577 25.025 4.79753 26.7063C4.53964 27.846 4.36987 29.0311 4.2882 30.2618C4.20654 31.4708 4.22373 32.6191 4.33978 33.7068ZM20.9027 23.1963C20.7609 21.866 21.21 20.7025 22.2502 19.7058C23.3032 18.7048 24.6679 18.1155 26.3442 17.9378C27.6422 17.7991 28.6781 17.9595 29.4517 18.4188C30.2426 18.8738 30.6896 19.6018 30.7927 20.6028C30.8744 21.3785 30.7433 22.0285 30.3995 22.5528C30.0771 23.0771 29.5979 23.3718 28.9617 23.4368C28.7253 23.4628 28.4997 23.4433 28.2848 23.3783C28.0871 23.3133 27.9022 23.1941 27.7303 23.0208C28.0484 22.7781 28.2891 22.438 28.4524 22.0003C28.6329 21.5626 28.6995 21.1206 28.6523 20.6743C28.5921 20.124 28.3858 19.7188 28.0333 19.4588C27.6766 19.1815 27.1887 19.0753 26.5698 19.1403C25.8348 19.2183 25.2761 19.487 24.8935 19.9463C24.5239 20.4056 24.3778 21.0058 24.4551 21.7468C24.5196 22.3491 24.7345 22.9103 25.0998 23.4303C25.4652 23.9286 26.2066 24.5808 27.3241 25.3868C28.8027 26.4355 29.7999 27.3758 30.3157 28.2078C30.8357 29.0268 31.1516 29.9693 31.2634 31.0353C31.4654 33.0026 30.9518 34.6428 29.7225 35.9558C28.4975 37.2731 26.832 38.0423 24.7259 38.2633C23.0496 38.441 21.685 38.194 20.6319 37.5223C19.5746 36.8506 18.9728 35.8085 18.8267 34.3958C18.6978 33.1521 18.9599 32.0948 19.6133 31.2238C20.2666 30.3528 21.1133 29.8631 22.1535 29.7548C22.8025 29.6855 23.3484 29.783 23.7911 30.0473C24.2338 30.2986 24.5003 30.6908 24.5905 31.2238C23.7008 31.3148 22.9959 31.6073 22.4758 32.1013C21.9729 32.5866 21.7623 33.2085 21.844 33.9668C21.9171 34.6905 22.2007 35.2625 22.695 35.6828C23.1893 36.1031 23.8126 36.2721 24.5647 36.1898C25.4544 36.0945 26.14 35.7088 26.6214 35.0328C27.1028 34.3525 27.2898 33.488 27.1823 32.4393C27.0963 31.629 26.8342 30.8706 26.3957 30.1643C25.9616 29.4623 25.1149 28.5501 23.8555 27.4278C22.7165 26.4355 21.9515 25.6446 21.5603 25.0553C21.1864 24.453 20.9672 23.8333 20.9027 23.1963ZM39.1032 33.3948C39.0731 33.0828 39.0667 32.6906 39.0839 32.2183C39.1226 31.746 39.2085 30.9876 39.3418 29.9433C39.4793 28.938 39.5588 28.2165 39.5803 27.7788C39.6061 27.3368 39.604 26.9706 39.5739 26.6803C39.5266 26.247 39.4062 25.9285 39.2128 25.7248C39.0366 25.5168 38.7938 25.428 38.4843 25.4583C38.076 25.5016 37.6999 25.7941 37.356 26.3358C37.0294 26.8601 36.8359 27.3953 36.7758 27.9413L35.8796 36.5343L32.1854 36.9243L34.0744 18.9453L37.8138 18.0288L37.111 24.6913C37.485 24.1626 37.9083 23.751 38.3811 23.4563C38.8496 23.166 39.3762 22.9883 39.9607 22.9233C40.8633 22.828 41.5897 23.0035 42.1399 23.4498C42.6857 23.9005 43.0081 24.5981 43.107 25.5428C43.1499 25.9415 43.1478 26.4225 43.1005 26.9858C43.0747 27.5275 42.9823 28.3725 42.8233 29.5208C42.6943 30.4265 42.6148 31.0635 42.5847 31.4318C42.5503 31.8001 42.546 32.1056 42.5718 32.3483C42.6105 32.708 42.7244 32.9745 42.9135 33.1478C43.1027 33.3038 43.3691 33.3623 43.713 33.3233C44.1385 33.28 44.491 33.0958 44.7703 32.7708C45.0454 32.4415 45.3312 31.7915 45.6278 30.8208L46.7045 30.7103C46.3263 32.37 45.7847 33.6375 45.0798 34.5128C44.3921 35.3881 43.511 35.8821 42.4364 35.9948C41.478 36.0988 40.7064 35.919 40.1219 35.4553C39.5373 34.9786 39.1978 34.2918 39.1032 33.3948ZM53.2227 22.8258L53.242 23.0078L53.3838 21.6103L57.0781 21.2138L56.1432 30.2033C56.1261 30.3636 56.1175 30.5131 56.1175 30.6518C56.1089 30.7731 56.1132 30.9031 56.1304 31.0418C56.169 31.4231 56.2829 31.6983 56.4721 31.8673C56.6612 32.0233 56.9363 32.0818 57.2973 32.0428C57.74 31.9951 58.1204 31.746 58.4385 31.2953C58.7522 30.8273 58.98 30.1925 59.1219 29.3908L60.1986 29.2803C59.816 30.9226 59.2315 32.1945 58.4449 33.0958C57.654 33.9841 56.687 34.489 55.5437 34.6103C54.8087 34.6883 54.1962 34.5431 53.7062 34.1748C53.2377 33.8065 52.9282 33.2475 52.7778 32.4978C52.3308 33.3471 51.8043 34.0036 51.1982 34.4673C50.6051 34.931 49.941 35.2018 49.206 35.2798C48.1444 35.3925 47.2697 35.1195 46.582 34.4608C45.9072 33.7978 45.501 32.8033 45.3635 31.4773C45.2603 30.511 45.299 29.4861 45.4795 28.4028C45.6601 27.3238 45.9631 26.325 46.3886 25.4063C46.913 24.323 47.5663 23.4693 48.3485 22.8453C49.1308 22.204 50.0184 21.8313 51.0113 21.7273C51.6775 21.658 52.2018 21.7251 52.5844 21.9288C52.9669 22.1151 53.1797 22.4141 53.2227 22.8258ZM52.5006 30.0928L53.1259 24.1193C53.1002 23.8593 52.982 23.6448 52.7713 23.4758C52.565 23.2895 52.2985 23.2136 51.9719 23.2483C51.0306 23.348 50.2763 24.2298 49.7089 25.8938C49.1545 27.5535 48.9589 29.159 49.1222 30.7103C49.191 31.3863 49.3371 31.8868 49.5606 32.2118C49.7841 32.5151 50.1344 32.643 50.6115 32.5953C51.0542 32.5476 51.4604 32.2855 51.8301 31.8088C52.2126 31.3365 52.4361 30.7645 52.5006 30.0928ZM63.3641 23.5798L62.6034 30.9833C62.6936 31.1653 62.829 31.2996 63.0095 31.3863C63.1901 31.4686 63.4007 31.499 63.6414 31.4773C64.7202 31.3603 65.5111 30.5695 66.014 29.1048C66.534 27.6401 66.6995 25.9848 66.5104 24.1388C66.433 23.4325 66.274 22.9081 66.0333 22.5658C65.7883 22.2278 65.4444 22.0826 65.0017 22.1303C64.6923 22.1606 64.3893 22.3145 64.0927 22.5918C63.7918 22.8475 63.549 23.1768 63.3641 23.5798ZM62.3971 32.9398L61.7201 39.3878L57.942 40.7333L60.1018 19.9268L63.7961 19.5368L63.5898 21.5193C63.9809 21.112 64.4129 20.787 64.8857 20.5443C65.3628 20.3016 65.8914 20.15 66.4717 20.0893C67.499 19.981 68.3135 20.2431 68.9152 20.8758C69.5169 21.5085 69.8887 22.4986 70.0306 23.8463C70.3056 26.481 69.9682 28.7213 69.0184 30.5673C68.0857 32.409 66.6372 33.4338 64.6729 33.6418C64.1271 33.6981 63.6564 33.6678 63.261 33.5508C62.887 33.4338 62.5991 33.2301 62.3971 32.9398ZM80.2365 21.0383C80.387 22.4683 79.9357 23.7791 78.8826 24.9708C77.8296 26.1451 76.5057 26.8601 74.9111 27.1158C74.924 27.4105 74.9305 27.638 74.9305 27.7983C74.9434 27.9326 74.9541 28.0518 74.9627 28.1558C75.0487 28.9488 75.2356 29.497 75.5236 29.8003C75.8331 30.095 76.3381 30.2055 77.0387 30.1318C77.8425 30.0495 78.5581 29.7916 79.1856 29.3583C79.8304 28.9076 80.5374 28.1341 81.3068 27.0378L82.1771 26.9468C81.3562 28.6715 80.4042 29.9931 79.321 30.9118C78.2422 31.8088 77.0086 32.331 75.6203 32.4783C74.3051 32.617 73.2692 32.3786 72.5127 31.7633C71.7735 31.1436 71.3329 30.1535 71.1911 28.7928C71.0922 27.8091 71.1373 26.7496 71.3265 25.6143C71.5199 24.479 71.8293 23.452 72.2549 22.5333C72.7792 21.4326 73.4498 20.579 74.2664 19.9723C75.1002 19.344 76.0652 18.9713 77.1612 18.8543C78.0982 18.7546 78.8203 18.8868 79.3275 19.2508C79.8475 19.5975 80.1506 20.1933 80.2365 21.0383ZM77.5093 20.1998C76.9248 20.2648 76.3897 20.8541 75.904 21.9678C75.4269 23.0641 75.1196 24.3598 74.982 25.8548C75.9663 25.6815 76.783 25.1333 77.432 24.2103C78.0939 23.27 78.369 22.2646 78.2572 21.1943C78.2185 20.8303 78.139 20.5681 78.0187 20.4078C77.8983 20.2475 77.7286 20.1781 77.5093 20.1998ZM84.9494 26.9143C85.0182 27.586 85.1644 28.0843 85.3879 28.4093C85.6114 28.717 85.9617 28.847 86.4388 28.7993C86.8686 28.7516 87.2683 28.509 87.6379 28.0713C88.0033 27.612 88.2311 27.0638 88.3214 26.4268L88.3085 26.2968L88.9467 20.2453C88.8651 19.9723 88.7232 19.7686 88.5212 19.6343C88.3321 19.4826 88.0914 19.422 87.7991 19.4523C86.8621 19.552 86.1078 20.4338 85.5361 22.0978C84.9817 23.7575 84.7861 25.363 84.9494 26.9143ZM89.0563 19.0298L89.0628 19.1338L89.701 13.0563L93.4404 12.1333L91.9511 26.4073C91.9339 26.5676 91.9232 26.7171 91.9189 26.8558C91.9146 26.9815 91.9189 27.1115 91.9318 27.2458C91.9705 27.5925 92.0736 27.8351 92.2413 27.9738C92.4261 28.0951 92.7076 28.1341 93.0858 28.0908C93.5286 28.0475 93.909 27.807 94.227 27.3693C94.5451 26.936 94.7772 26.3445 94.9233 25.5948L96 25.4843C95.6304 27.0746 95.0587 28.3031 94.285 29.1698C93.5286 30.0321 92.5443 30.5283 91.3322 30.6583C90.6273 30.732 90.0342 30.6041 89.5528 30.2748C89.0843 29.9195 88.7619 29.3951 88.5857 28.7018C88.1559 29.5468 87.6444 30.2011 87.0512 30.6648C86.4581 31.1285 85.7854 31.4015 85.0333 31.4838C83.9759 31.5965 83.1012 31.3235 82.4092 30.6648C81.7344 30.0018 81.3283 29.0073 81.1907 27.6813C81.0919 26.715 81.1327 25.6901 81.3132 24.6068C81.4937 23.5278 81.7946 22.529 82.2158 21.6103C82.7445 20.527 83.4 19.6733 84.1822 19.0493C84.9602 18.408 85.8456 18.0353 86.8385 17.9313C87.5047 17.862 88.0312 17.9291 88.4181 18.1328C88.8006 18.3191 89.0134 18.6181 89.0563 19.0298Z"
                        fill="currentColor"
                      />
                    </svg>
                  </Link>
                </div>
              )}
          </div>

          {/* Navigation links */}
          <nav className={styles.headerNav}>
            {lastDomain === "medicine" ? (
              <Link to={`/pricing?lang=${entry.language}&return_to=${encodeURIComponent(medicineReturn)}`} className={styles.chatLoginLink}>
                {entry.language === "ar" ? "خطط الوصول" : "Access plans"}
              </Link>
            ) : <HeaderQBankLink />}
          </nav>

          <div className={styles.headerRightContainer}>
            {user ? (
              <AccountMenu />
            ) : (
              <Link to="/login" state={{ from: lastDomain === "medicine" ? medicineReturn : "/chat" }} className={styles.chatLoginLink}>
                {lastDomain === "medicine" && entry.language === "ar" ? "تسجيل الدخول" : "Log in"}
              </Link>
            )}
          </div>
        </div>

        {/* Sidebar */}
        {user && (
          <Sidebar
            onItemSelect={handleMobileItemSelect}
            refreshTrigger={refreshTrigger}
            selectedItemId={selectedContent?.id}
            isCollapsed={!isSidebarVisible}
            onToggleCollapse={handleSidebarCollapse}
            isMobileVisible={isMobileSidebarVisible}
            screenSize={screenSize}
            onMobileClose={() => setIsMobileSidebarVisible(false)}
            activeThreads={activeThreadProcessing}
            subscriptionPlan={subscriptionPlan}
          />
        )}

        {/* Content area */}
        <div className={`${styles.contentArea} ${getContentAreaClass()}`}>
          {selectedContent ? (
            <ContentContainer
              contentType={selectedContent.type}
              selectedItem={selectedContent}
              realTimeData={realTimeQuestionData}
              isTransitioning={isTransitioning}
              threadMessages={threadMessages}
              isLoadingMessages={isLoadingMessages}
              processingStatus={visibleProcessingStatus}
              scrollToBottomRequest={scrollToBottomRequest}
              reactionPendingMessageIds={reactionPendingMessageIds}
              reactionErrors={reactionErrors}
              onReactionChange={handleReactionChange}
            />
          ) : (
            <div className={styles.welcomeMessage}>
              <h2 className={styles.welcomeTitle}>Welcome to the chat!</h2>
              <p className={styles.welcomeDescription}>
                Select a conversation from the sidebar or start a new one
              </p>
            </div>
          )}
        </div>

        {/* Input bar area */}
        <div className={`${styles.inputBarArea} ${getInputBarAreaClass()}`}>
          <InputBar
            onSend={handleMessageSubmit}
            isProcessing={inputIsProcessing}
            credits={credits}
            subscriptionPlan={subscriptionPlan}
            initialDomain={lastDomain}
            onDomainChange={setLastDomain}
          />
        </div>
      </div>
    </div>
  );
};

export default Chat;
